import React, { useEffect, useRef, useState, useMemo, forwardRef, useImperativeHandle } from "react";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { OrbitControls, Html } from "@react-three/drei";
import { Vector3, DoubleSide } from "three";
import * as THREE from "three";
import { AppToolbarButton } from "./WhatsNew";
import type { OrbitControls as OrbitControlsImpl } from "three-stdlib";
import { TunnelCenterline } from "./TunnelCenterline";
import { TunnelProfiles } from "./TunnelProfiles";
import { StationMarkers } from "./StationMarkers";
import type { CenterlineData } from "./useTunnelCenterline";
import type { ProfileAssignment } from "./tunnelProfileShapes";
import { WeaknessZoneLines3D } from "./WeaknessZoneLines3D";
import { TunnelDraw3D, type TunnelDraw3DHandle } from "./TunnelDraw3D";
import type { DrawToolType } from "./drawings3D";
import { DRAW_TOOL_CONFIG } from "./drawings3D";
import { PointCloud3D } from "./PointCloud3D";
import { Boreholes3D, type SalvoJsonData, type MwdRawScanData, type MwdSensorName, type CategoryColorMap, MWD_SENSORS } from "./Boreholes3D";
import { ClipPlane3D } from "./ClipPlane3D";
import { MWDRockType3D, getMWDRockTypes, type MWDData } from "./MWDRockType3D";
import { RBFVolume3D } from "./RBFVolume3D";
import { generateRBFVolume, extractSurfaceFromGrid } from "./generateRBFVolume";
import type { ScalarGrid } from "./rbfInterpolant";
import { TunnelPhotos3D } from "./TunnelPhotos3D";
import type { TunnelPhoto, TunnelPhotoMeta } from "./tunnelPhotos";
import { loadAllPhotos, savePhotoMetas, savePhotoImage, deletePhotoImage, loadPhotoMetas, fileToDataUrl, migrateFromLocalStorage } from "./tunnelPhotos";
import { loadRBFLayers, saveRBFLayer, deleteRBFLayer, updateRBFLayerVisibility, type RBFLayer } from "./rbfLayerStore";
import { worldToTunnelPoint3D } from "./drawings3D";
import { useProject } from "./ProjectContext";
import { storageKey, type DataTableStatus } from "./projectConfig";
import { usePointcloudCacheStatus } from "./usePointcloudCache";

const _q1 = new THREE.Quaternion(-Math.sqrt(0.5), 0, 0, Math.sqrt(0.5));

/** Build device orientation quaternion — proven approach from dip recorder */
function deviceOrientationQuat(alpha: number, beta: number, gamma: number): THREE.Quaternion {
  const euler = new THREE.Euler(
    THREE.MathUtils.degToRad(beta),
    THREE.MathUtils.degToRad(alpha),
    -THREE.MathUtils.degToRad(gamma),
    "YXZ",
  );
  const q = new THREE.Quaternion().setFromEuler(euler);
  q.multiply(_q1);
  return q;
}

/** Component inside Canvas that updates OrbitControls target based on station */
function CameraController({ data, station, ctrlHeld, cameraQuatRef, centerlineLock, deviceOrientationRef, mouseLookRef, shouldRecalibrate, drawMode, zoomFovRef, onStationChange, cameraKey, clipDragging }: {
  data: CenterlineData;
  station: number;
  ctrlHeld: boolean;
  cameraQuatRef: React.RefObject<THREE.Quaternion>;
  centerlineLock: boolean;
  deviceOrientationRef: React.RefObject<{ alpha: number; beta: number; gamma: number } | null>;
  mouseLookRef: React.RefObject<{ yaw: number; pitch: number } | null>;
  shouldRecalibrate: React.RefObject<boolean>;
  drawMode: boolean;
  zoomFovRef: React.RefObject<number>;
  onStationChange: (station: number) => void;
  cameraKey: string;
  clipDragging: React.RefObject<boolean>;
}) {
  const controlsRef = useRef<OrbitControlsImpl>(null);
  const { camera } = useThree();
  const initialized = useRef(false);
  const refDeviceQuat = useRef<THREE.Quaternion | null>(null);
  const refTunnelQuat = useRef<THREE.Quaternion | null>(null);
  const savedCameraState = useRef<{ pos: THREE.Vector3; quat: THREE.Quaternion; fov: number; target: THREE.Vector3 } | null>(null);

  // ── Pan-along-centerline: smooth station scrubbing via middle-mouse / right-drag ──
  const stationTargetRef = useRef(station);
  const smoothStationRef = useRef(station);
  const onStationChangeRef = useRef(onStationChange);
  onStationChangeRef.current = onStationChange;
  const isPanDragging = useRef(false);

  // Keep target in sync when station changes externally (slider, ctrl+wheel)
  // but NOT when we ourselves are driving the station via pan
  useEffect(() => {
    if (!isPanDragging.current) {
      stationTargetRef.current = station;
      smoothStationRef.current = station;
    }
  }, [station]);

  // Smooth interpolation: damp station toward target every frame.
  // Move camera smoothly via ref; only update React state at ~1m granularity
  // to avoid excessive re-renders that cause Html label flicker.
  useFrame((_, delta) => {
    const target = stationTargetRef.current;
    const current = smoothStationRef.current;
    if (Math.abs(target - current) < 0.05) {
      smoothStationRef.current = target;
      return;
    }
    const next = THREE.MathUtils.damp(current, target, 12, delta);
    smoothStationRef.current = next;

    // Move camera smoothly without triggering React re-render
    if (controlsRef.current && initialized.current && !centerlineLock) {
      const t = Math.max(0, Math.min(1, next / data.totalLength));
      const point = data.curve.getPointAt(t);
      const prevTarget = controlsRef.current.target;
      const d = point.clone().sub(prevTarget);
      camera.position.add(d);
      controlsRef.current.target.copy(point);
      controlsRef.current.update();
    }

    // Update React state less frequently for UI (slider, station display)
    if (Math.abs(next - station) >= 0.5) {
      onStationChangeRef.current(next);
    }
  });

  // Pointer events: pan along centerline to scrub station.
  // Two-finger drag everywhere (both orbit and look-around mode).
  // Desktop: right-click / middle-click drag in orbit mode.
  const { gl } = useThree();
  useEffect(() => {
    if (drawMode) return;
    const canvas = gl.domElement;

    // Given a screen-space delta (px), compute station delta.
    // In lock mode: skip projection (tangent goes into screen → near-zero length),
    // just map vertical swipe directly to station change.
    const applyDragToStation = (dx: number, dy: number) => {
      if (centerlineLock) {
        // Simple vertical mapping: swipe up = move forward, swipe down = move back
        // Use total drag magnitude projected onto vertical for consistent feel
        const pxPerMeter = canvas.clientHeight / 80; // full swipe ≈ 80m
        const delta = dy / pxPerMeter;
        stationTargetRef.current = Math.max(0, Math.min(data.totalLength,
          stationTargetRef.current + delta));
        return;
      }

      // Orbit mode: project drag onto centerline tangent in screen space
      const t = Math.max(0, Math.min(1, stationTargetRef.current / data.totalLength));
      const tAhead = Math.min(1, t + 0.002);
      const tBehind = Math.max(0, t - 0.002);
      const ahead = data.curve.getPointAt(tAhead);
      const behind = data.curve.getPointAt(tBehind);
      const tangent = ahead.clone().sub(behind).normalize();

      const pos = data.curve.getPointAt(t);
      const screenPos = pos.clone().project(camera);
      const screenAhead = pos.clone().add(tangent).project(camera);
      const screenTangentX = screenAhead.x - screenPos.x;
      const screenTangentY = screenAhead.y - screenPos.y;
      const screenTangentLen = Math.sqrt(screenTangentX * screenTangentX + screenTangentY * screenTangentY);
      if (screenTangentLen < 0.0001) return;

      const ndx = dx / canvas.clientWidth * 2;
      const ndy = -dy / canvas.clientHeight * 2;
      const dot = (ndx * screenTangentX + ndy * screenTangentY) / screenTangentLen;
      const dist = camera.position.distanceTo(pos);
      const sensitivity = dist * 0.8;

      // Negate: drag feels like grabbing the tunnel and pulling (natural scroll direction)
      stationTargetRef.current = Math.max(0, Math.min(data.totalLength,
        stationTargetRef.current - dot * sensitivity));
    };

    // ── Mouse drag: right-click / middle-click (orbit mode only) ──
    let mouseDragging = false;
    let lastMouseX = 0;
    let lastMouseY = 0;

    const onMouseDown = (e: PointerEvent) => {
      if (e.pointerType === "touch") return;
      if (centerlineLock) return;
      if (e.button !== 1 && e.button !== 2) return;
      e.preventDefault();
      mouseDragging = true;
      isPanDragging.current = true;
      lastMouseX = e.clientX;
      lastMouseY = e.clientY;
      canvas.setPointerCapture(e.pointerId);
    };
    const onMouseMove = (e: PointerEvent) => {
      if (!mouseDragging || e.pointerType === "touch") return;
      const dx = e.clientX - lastMouseX;
      const dy = e.clientY - lastMouseY;
      lastMouseX = e.clientX;
      lastMouseY = e.clientY;
      applyDragToStation(dx, dy);
    };
    const onMouseUp = (e: PointerEvent) => {
      if (e.pointerType === "touch") return;
      if (e.button !== 1 && e.button !== 2) return;
      mouseDragging = false;
      isPanDragging.current = false;
      canvas.releasePointerCapture(e.pointerId);
    };
    const onContext = (e: Event) => { e.preventDefault(); };

    // ── Touch: two-finger drag (both modes) ──
    let touchDragging = false;
    let lastTouchMidX = 0;
    let lastTouchMidY = 0;
    let lastTouchDist = 0;

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        touchDragging = true;
        isPanDragging.current = true;
        lastTouchMidX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        lastTouchMidY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        lastTouchDist = Math.sqrt(dx * dx + dy * dy);
      }
    };
    const onTouchMove = (e: TouchEvent) => {
      if (!touchDragging || e.touches.length !== 2) return;
      const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;

      // Only treat as pan if the fingers aren't pinching (distance stable)
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const pinchRatio = lastTouchDist > 0 ? dist / lastTouchDist : 1;
      const isPinch = Math.abs(pinchRatio - 1) > 0.03;

      if (!isPinch) {
        const dmx = midX - lastTouchMidX;
        const dmy = midY - lastTouchMidY;
        applyDragToStation(dmx, dmy);
      }

      lastTouchMidX = midX;
      lastTouchMidY = midY;
      lastTouchDist = dist;
    };
    const onTouchEnd = () => {
      touchDragging = false;
      isPanDragging.current = false;
    };

    canvas.addEventListener("pointerdown", onMouseDown);
    canvas.addEventListener("pointermove", onMouseMove);
    canvas.addEventListener("pointerup", onMouseUp);
    canvas.addEventListener("contextmenu", onContext);
    canvas.addEventListener("touchstart", onTouchStart, { passive: true });
    canvas.addEventListener("touchmove", onTouchMove, { passive: true });
    canvas.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      canvas.removeEventListener("pointerdown", onMouseDown);
      canvas.removeEventListener("pointermove", onMouseMove);
      canvas.removeEventListener("pointerup", onMouseUp);
      canvas.removeEventListener("contextmenu", onContext);
      canvas.removeEventListener("touchstart", onTouchStart);
      canvas.removeEventListener("touchmove", onTouchMove);
      canvas.removeEventListener("touchend", onTouchEnd);
    };
  }, [data, camera, gl, centerlineLock, drawMode]);

  // Save camera state when entering lock mode, restore when leaving
  useEffect(() => {
    if (centerlineLock) {
      const fov = camera instanceof THREE.PerspectiveCamera ? camera.fov : 50;
      savedCameraState.current = {
        pos: camera.position.clone(),
        quat: camera.quaternion.clone(),
        fov,
        target: controlsRef.current ? controlsRef.current.target.clone() : new Vector3(),
      };
    } else if (savedCameraState.current) {
      const s = savedCameraState.current;
      camera.position.copy(s.pos);
      camera.quaternion.copy(s.quat);
      if (camera instanceof THREE.PerspectiveCamera) {
        camera.fov = s.fov;
        camera.updateProjectionMatrix();
      }
      if (controlsRef.current) {
        controlsRef.current.target.copy(s.target);
        controlsRef.current.update();
      }
      savedCameraState.current = null;
      refDeviceQuat.current = null;
      refTunnelQuat.current = null;
    }
  }, [centerlineLock, camera]);

  // Centerline lock mode: apply device orientation OR mouse look to camera rotation
  useFrame(() => {
    if (!centerlineLock) return;

    const o = deviceOrientationRef.current;
    const m = mouseLookRef.current;
    if (!o && !m) return;

    // Use smooth station ref for buttery movement during pan scrubbing
    const effectiveStation = isPanDragging.current ? smoothStationRef.current : station;
    const t = Math.max(0, Math.min(1, effectiveStation / data.totalLength));
    const point = data.curve.getPointAt(t);
    const tAhead = Math.min(1, t + 0.005);
    const ahead = data.curve.getPointAt(tAhead);

    // Tunnel forward quaternion
    const forward = ahead.clone().sub(point).normalize();
    const worldUp = new Vector3(0, 1, 0);
    const right = new Vector3().crossVectors(forward, worldUp).normalize();
    const up = new Vector3().crossVectors(right, forward).normalize();
    const basisMatrix = new THREE.Matrix4().makeBasis(right, up, forward.clone().negate());
    const tunnelQuat = new THREE.Quaternion().setFromRotationMatrix(basisMatrix);

    // Pin camera position regardless of input source
    camera.position.copy(point).add(up);
    (camera as THREE.PerspectiveCamera).fov = zoomFovRef.current;
    (camera as THREE.PerspectiveCamera).updateProjectionMatrix();

    if (o) {
      // Device orientation path
      const deviceQuat = deviceOrientationQuat(o.alpha, o.beta, o.gamma);

      // Capture reference on first frame, or recalibrate
      if (!refDeviceQuat.current || !refTunnelQuat.current || shouldRecalibrate.current) {
        refDeviceQuat.current = deviceQuat.clone();
        refTunnelQuat.current = tunnelQuat.clone();
        shouldRecalibrate.current = false;
        return;
      }

      // The mapping is: result = tunnelQuat * inverse(deviceQuat_ref) * deviceQuat_now
      camera.quaternion
        .copy(refTunnelQuat.current)
        .multiply(refDeviceQuat.current.clone().invert())
        .multiply(deviceQuat);
    } else if (m) {
      // Mouse look path: yaw rotates around tunnel up, pitch around tunnel right.
      if (shouldRecalibrate.current) {
        m.yaw = 0;
        m.pitch = 0;
        shouldRecalibrate.current = false;
      }
      const yawQuat = new THREE.Quaternion().setFromAxisAngle(new Vector3(0, 1, 0), m.yaw);
      const pitchQuat = new THREE.Quaternion().setFromAxisAngle(new Vector3(1, 0, 0), m.pitch);
      camera.quaternion.copy(tunnelQuat).multiply(yawQuat).multiply(pitchQuat);
    }
  });

  // Sync quaternion to shared ref for compass
  useFrame(() => {
    if (cameraQuatRef.current) cameraQuatRef.current.copy(camera.quaternion);
  });

  // Save camera state on changes (debounced via requestIdleCallback fallback)
  useEffect(() => {
    const id = setTimeout(() => {
      if (!controlsRef.current) return;
      const t = controlsRef.current.target;
      localStorage.setItem(cameraKey, JSON.stringify({
        station,
        pos: [camera.position.x, camera.position.y, camera.position.z],
        target: [t.x, t.y, t.z],
      }));
    }, 300);
    return () => clearTimeout(id);
  }, [station, camera]);

  useEffect(() => {
    if (!controlsRef.current) return;
    const t = Math.max(0, Math.min(1, station / data.totalLength));
    const point = data.curve.getPointAt(t);

    if (centerlineLock) {
      // useFrame handles camera position/rotation in lock mode
      initialized.current = true;
      return;
    }

    // When pan-dragging, useFrame handles smooth camera movement — skip here
    if (isPanDragging.current) return;

    if (!initialized.current) {
      // Try restoring saved camera state
      let restored = false;
      try {
        const saved = JSON.parse(localStorage.getItem(cameraKey) || "");
        if (saved.pos && saved.target) {
          camera.position.set(saved.pos[0], saved.pos[1], saved.pos[2]);
          controlsRef.current.target.set(saved.target[0], saved.target[1], saved.target[2]);
          restored = true;
        }
      } catch { /* ignore */ }

      if (!restored) {
        // Default: start at pf0, looking forward along the tunnel with a slight upward angle
        const tAhead = Math.min(1, 0.05);
        const ahead = data.curve.getPointAt(tAhead);
        const dir = ahead.clone().sub(point).normalize();
        const side = dir.clone().cross(new Vector3(0, 1, 0)).normalize();
        camera.position.set(point.x - dir.x * 5 + side.x * 8, point.y + 8, point.z - dir.z * 5 + side.z * 8);
        controlsRef.current.target.set(ahead.x, ahead.y + 2, ahead.z);
      }
      initialized.current = true;
    } else {
      const delta = point.clone().sub(controlsRef.current.target);
      camera.position.add(delta);
      controlsRef.current.target.copy(point);
    }

    controlsRef.current.update();
  }, [station, data, camera, centerlineLock]);

  // Save on orbit/pan/zoom
  const handleChange = () => {
    if (!controlsRef.current) return;
    const t = controlsRef.current.target;
    localStorage.setItem(cameraKey, JSON.stringify({
      station,
      pos: [camera.position.x, camera.position.y, camera.position.z],
      target: [t.x, t.y, t.z],
    }));
  };

  // Rotation center indicator — small world-axis gimbal at orbit target (hidden in lock mode)
  const gimbalRef = useRef<THREE.Group>(null);

  // Disable orbit controls when clip plane gizmo is being dragged
  useFrame(() => {
    if (controlsRef.current) {
      controlsRef.current.enabled = !clipDragging.current;
    }
  });

  useFrame(() => {
    if (controlsRef.current && gimbalRef.current) {
      const target = controlsRef.current.target;
      gimbalRef.current.position.copy(target);
      // Constant screen size: scale by distance to camera
      const dist = camera.position.distanceTo(target);
      const desired = dist * 0.06; // ≈6% of distance — comfortable focal-point size
      gimbalRef.current.scale.setScalar(desired);
      gimbalRef.current.visible = !centerlineLock;
    }
  });

  return (
    <>
      <group ref={gimbalRef}>
        {/* World-axis gimbal pinned to orbit pivot.
            Data: +X=East, +Y=Up, +Z=North (left-handed ENU).
            Three.js renders right-handed, so we flip X visually so East appears to the right of North. */}
        <arrowHelper args={[new Vector3(-1, 0, 0), new Vector3(0, 0, 0), 1, 0xffaa44, 0.28, 0.16]} />
        <arrowHelper args={[new Vector3(0, 1, 0), new Vector3(0, 0, 0), 1, 0x44ff44, 0.28, 0.16]} />
        <arrowHelper args={[new Vector3(0, 0, 1), new Vector3(0, 0, 0), 1, 0xff4444, 0.28, 0.16]} />
        {!centerlineLock && (
          <>
            <Html position={[-1.25, 0, 0]} center zIndexRange={[1, 0]} style={{ pointerEvents: "none", userSelect: "none" }}>
              <span style={{ color: "#ffaa44", fontSize: "11px", fontWeight: 700, fontFamily: "system-ui", textShadow: "0 0 3px rgba(0,0,0,0.9)" }}>E</span>
            </Html>
            <Html position={[0, 1.25, 0]} center zIndexRange={[1, 0]} style={{ pointerEvents: "none", userSelect: "none" }}>
              <span style={{ color: "#44ff44", fontSize: "11px", fontWeight: 700, fontFamily: "system-ui", textShadow: "0 0 3px rgba(0,0,0,0.9)" }}>Up</span>
            </Html>
            <Html position={[0, 0, 1.25]} center zIndexRange={[1, 0]} style={{ pointerEvents: "none", userSelect: "none" }}>
              <span style={{ color: "#ff4444", fontSize: "11px", fontWeight: 700, fontFamily: "system-ui", textShadow: "0 0 3px rgba(0,0,0,0.9)" }}>N</span>
            </Html>
          </>
        )}
      </group>
      <OrbitControls
        ref={controlsRef}
        enableZoom={!ctrlHeld && !centerlineLock && !drawMode}
        enablePan={false}
        enableRotate={!centerlineLock && !drawMode}
        onChange={handleChange}
      />
    </>
  );
}

/** HUD compass — syncs to main camera quaternion via shared ref */
function CompassScene({ quatRef }: { quatRef: React.RefObject<THREE.Quaternion> }) {
  const { camera } = useThree();

  useFrame(() => {
    if (!quatRef.current) return;
    // Camera forward is local -Z. Position camera along its forward direction (away from origin),
    // then look back at origin. This shows axes from the viewer's perspective (non-mirrored).
    const dist = 4;
    const forward = new Vector3(0, 0, -1).applyQuaternion(quatRef.current);
    camera.position.copy(forward.multiplyScalar(dist));
    camera.up.set(0, 1, 0).applyQuaternion(quatRef.current);
    camera.lookAt(0, 0, 0);
  });

  return (
    <>
      <ambientLight intensity={1} />
      {/* Compass axes in world coordinates: X=East, Y=Up, Z=North */}
      <arrowHelper args={[new Vector3(1, 0, 0), new Vector3(0, 0, 0), 1, 0xffaa44]} />
      <arrowHelper args={[new Vector3(0, 1, 0), new Vector3(0, 0, 0), 1, 0x44ff44]} />
      <arrowHelper args={[new Vector3(0, 0, 1), new Vector3(0, 0, 0), 1, 0xff4444]} />
      <Html position={[1.3, 0, 0]} center zIndexRange={[1, 0]} style={{ pointerEvents: "none", userSelect: "none" }}>
        <span style={{ color: "#ffaa44", fontSize: "11px", fontWeight: 700, fontFamily: "system-ui" }}>E</span>
      </Html>
      <Html position={[0, 1.3, 0]} center zIndexRange={[1, 0]} style={{ pointerEvents: "none", userSelect: "none" }}>
        <span style={{ color: "#44ff44", fontSize: "11px", fontWeight: 700, fontFamily: "system-ui" }}>Up</span>
      </Html>
      <Html position={[0, 0, 1.3]} center zIndexRange={[1, 0]} style={{ pointerEvents: "none", userSelect: "none" }}>
        <span style={{ color: "#ff4444", fontSize: "11px", fontWeight: 700, fontFamily: "system-ui" }}>N</span>
      </Html>
    </>
  );
}

export interface PhotoCaptureHandle {
  /** Raycast center-screen against tunnel mesh, return hit info or null */
  getHit: () => {
    point: THREE.Vector3;
    dir: THREE.Vector3;
    distance: number;
    fov: number;
    /** Camera's combined projection * view matrix at capture time */
    projViewMatrix: number[];
    /** Camera world position */
    camPos: THREE.Vector3;
  } | null;
}

/** Helper inside Canvas to raycast center-screen for photo placement */
const PhotoCapture = forwardRef<PhotoCaptureHandle, {}>(function PhotoCapture(_, ref) {
  const { camera, scene } = useThree();
  const raycaster = useMemo(() => new THREE.Raycaster(), []);

  useImperativeHandle(ref, () => ({
    getHit: () => {
      // Raycast from camera center (0,0 in NDC) into the scene
      raycaster.setFromCamera({ x: 0, y: 0 } as any, camera);
      const hits = raycaster.intersectObjects(scene.children, true);
      // Find first hit that's an actual mesh (skip helpers, lines, etc.)
      for (const hit of hits) {
        if (hit.object.type === "Mesh" && hit.face) {
          const dir = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
          const fov = (camera as THREE.PerspectiveCamera).fov ?? 75;
          // Compute projection * view matrix for projective texturing
          const projView = new THREE.Matrix4().multiplyMatrices(
            camera.projectionMatrix,
            camera.matrixWorldInverse,
          );
          return {
            point: hit.point.clone(),
            dir,
            distance: hit.distance,
            fov,
            projViewMatrix: projView.elements.slice(),
            camPos: camera.position.clone(),
          };
        }
      }
      return null;
    },
  }), [camera, scene, raycaster]);

  return null;
});

/** Controls scene background — transparent when AR photo overlay is active */
function SceneBackground({ transparent }: { transparent: boolean }) {
  const { scene, gl } = useThree();
  useEffect(() => {
    if (transparent) {
      scene.background = null;
      gl.setClearColor(0x000000, 0);
    } else {
      scene.background = null;
      gl.setClearColor(0x000000, 1);
    }
  }, [transparent, scene, gl]);
  return null;
}

function Scene({ data, station, ctrlHeld, profileAssignments, cameraQuatRef, centerlineLock, deviceOrientationRef, mouseLookRef, shouldRecalibrate, drawMode, drawToolType, drawRef, brushScale, onBrushScaleChange, enabledScans, onPcLoadingChange, salvoData, mwdRawData, mwdRawSensor, mwdClampedRange, categoryColors, disabledCategories, photos, onPhotosChange, photoCaptureRef, arPhotoActive, zoomFovRef, onStationChange, cameraKey, clipPlaneActive, clipPlaneFlipped, clipPlaneMatrix, onClipPlaneMatrixChange, showMWD, mwdOpacity, rbfGeometry, rbfOpacity, rbfSavedLayers, stereonetFilter, stereonetFilterIndex }: {
  data: CenterlineData;
  station: number;
  ctrlHeld: boolean;
  profileAssignments: ProfileAssignment[];
  cameraQuatRef: React.RefObject<THREE.Quaternion>;
  centerlineLock: boolean;
  deviceOrientationRef: React.RefObject<{ alpha: number; beta: number; gamma: number } | null>;
  mouseLookRef: React.RefObject<{ yaw: number; pitch: number } | null>;
  shouldRecalibrate: React.RefObject<boolean>;
  drawMode: boolean;
  drawToolType: DrawToolType;
  drawRef: React.RefObject<TunnelDraw3DHandle | null>;
  brushScale: number;
  onBrushScaleChange: (scale: number) => void;
  enabledScans: ReadonlySet<number>;
  onPcLoadingChange: (loading: Set<number>) => void;
  salvoData: SalvoJsonData | null;
  photos: TunnelPhoto[];
  onPhotosChange: (photos: TunnelPhoto[]) => void;
  photoCaptureRef: React.RefObject<PhotoCaptureHandle | null>;
  arPhotoActive: boolean;
  zoomFovRef: React.RefObject<number>;
  onStationChange: (station: number) => void;
  cameraKey: string;
  clipPlaneActive: boolean;
  clipPlaneFlipped: boolean;
  clipPlaneMatrix?: number[];
  onClipPlaneMatrixChange?: (elements: number[]) => void;
  showMWD: boolean;
  mwdOpacity: number;
  rbfGeometry: THREE.BufferGeometry | null;
  rbfOpacity: number;
  rbfSavedLayers: RBFLayer[];
  stereonetFilter?: { x: number; y: number; r: number };
  stereonetFilterIndex?: number;
  mwdRawData?: Map<number, MwdRawScanData>;
  mwdRawSensor?: MwdSensorName | null;
  mwdClampedRange?: { min: number; max: number } | null;
  categoryColors?: CategoryColorMap | null;
  disabledCategories?: ReadonlySet<string> | null;
}) {
  const clipDragging = useRef(false);
  return (
    <>
      <SceneBackground transparent={arPhotoActive} />
      <ambientLight intensity={arPhotoActive ? 1.5 : 0.5} />
      <directionalLight position={[100, 200, 100]} intensity={arPhotoActive ? 2 : 1} />
      <TunnelCenterline data={data} arMode={arPhotoActive} />
      <TunnelProfiles data={data} profileAssignments={profileAssignments} arMode={arPhotoActive} />
      <WeaknessZoneLines3D data={data} profileAssignments={profileAssignments} />
      <TunnelDraw3D ref={drawRef} data={data} profileAssignments={profileAssignments} drawMode={drawMode} drawToolType={drawToolType} brushScale={brushScale} onBrushScaleChange={onBrushScaleChange} />
      <StationMarkers data={data} />
      <PointCloud3D data={data} enabledIndices={enabledScans} centerlineLock={centerlineLock} onLoadingChange={onPcLoadingChange} stereonetFilter={stereonetFilter} stereonetFilterIndex={stereonetFilterIndex} />
      <Boreholes3D data={data} salvoData={salvoData} mwdRawData={mwdRawData} activeSensor={mwdRawSensor} clampedRange={mwdClampedRange} categoryColors={categoryColors} disabledCategories={disabledCategories} />
      <TunnelPhotos3D data={data} profileAssignments={profileAssignments} photos={photos} onPhotosChange={onPhotosChange} />
      <PhotoCapture ref={photoCaptureRef} />
      <CameraController data={data} station={station} ctrlHeld={ctrlHeld} cameraQuatRef={cameraQuatRef} centerlineLock={centerlineLock} deviceOrientationRef={deviceOrientationRef} mouseLookRef={mouseLookRef} shouldRecalibrate={shouldRecalibrate} drawMode={drawMode} zoomFovRef={zoomFovRef} onStationChange={onStationChange} cameraKey={cameraKey} clipDragging={clipDragging} />
      <ClipPlane3D active={clipPlaneActive} flipped={clipPlaneFlipped} initialMatrix={clipPlaneMatrix} onDragStart={() => { clipDragging.current = true; }} onDragEnd={() => { clipDragging.current = false; }} onMatrixChange={onClipPlaneMatrixChange} />
      <MWDRockType3D data={data} visible={showMWD} profileAssignments={profileAssignments} opacity={mwdOpacity} />
      <RBFVolume3D geometry={rbfGeometry} opacity={rbfOpacity / 100} />
      {rbfSavedLayers.map((layer) => (
        <RBFVolume3D key={layer.id} geometry={layer.geometry} visible={layer.visible} opacity={layer.opacity} color={layer.color} />
      ))}
    </>
  );
}

const RING_SPACING = 10;
const NGI_BLUE = "#002855";
const NGI_RED = "#ab2328";
const NGI_STEEL = "#75787b";
const HUD_TEXT = "#f7f8fb";
const GLASS_PANEL = "linear-gradient(180deg, rgba(255,255,255,0.14), rgba(255,255,255,0.08))";

function iconBadgeStyle(activeColor?: string): React.CSSProperties {
  return {
    width: 28,
    height: 28,
    borderRadius: 10,
    display: "grid",
    placeItems: "center",
    background: activeColor ? activeColor + "26" : "rgba(255,255,255,0.08)",
    border: `1px solid ${activeColor ? activeColor + "44" : "rgba(255,255,255,0.12)"}`,
    color: "#f6f8fb",
    fontSize: "0.66rem",
    fontWeight: 800,
    letterSpacing: "0.04em",
    flexShrink: 0,
  };
}

function sectionBtnStyle(active: boolean, activeColor?: string, compact?: boolean): React.CSSProperties {
  const color = activeColor ?? NGI_BLUE;
  return {
    minHeight: compact ? 34 : 42,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: compact ? 8 : 10,
    width: "100%",
    background: active ? color + "24" : "rgba(255,255,255,0.06)",
    color: active ? "#f6f8fb" : "rgba(247,248,251,0.84)",
    border: `1px solid ${active ? color + "66" : "rgba(255,255,255,0.1)"}`,
    borderRadius: "10px",
    cursor: "pointer",
    padding: compact ? "0.38rem 0.5rem" : "0.52rem 0.65rem",
    textAlign: "left",
    boxShadow: active ? `0 0 0 1px ${color}22 inset` : "none",
    transition: "all 0.15s ease",
  };
}

const sectionCardStyle: React.CSSProperties = {
  pointerEvents: "auto",
  padding: "0.35rem",
  borderRadius: 16,
  background: GLASS_PANEL,
  border: "1px solid rgba(255,255,255,0.14)",
  backdropFilter: "blur(14px)",
  WebkitBackdropFilter: "blur(14px)",
  boxShadow: "0 12px 36px rgba(0,0,0,0.18)",
};

const sectionGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: "0.35rem",
  padding: "0 0.15rem 0.15rem",
};

function SidebarItem({
  icon,
  title,
  subtitle,
  active,
  activeColor,
  onClick,
  compact,
  expanded,
}: {
  icon: string;
  title: string;
  subtitle: string;
  active: boolean;
  activeColor?: string;
  onClick: () => void;
  compact: boolean;
  expanded?: boolean;
}) {
  return (
    <button type="button" onClick={onClick} style={sectionBtnStyle(active, activeColor, compact)}>
      <div style={{ display: "flex", alignItems: "center", gap: compact ? 8 : 10, minWidth: 0, flex: 1 }}>
        <span aria-hidden="true" style={iconBadgeStyle(activeColor)}>{icon}</span>
        <span style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
          <span style={{ fontSize: compact ? "0.74rem" : "0.78rem", fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</span>
          <span style={{ fontSize: compact ? "0.62rem" : "0.65rem", opacity: 0.62, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{subtitle}</span>
        </span>
      </div>
      <span
        style={{
          flexShrink: 0,
          fontSize: expanded !== undefined ? "0.78rem" : "0.62rem",
          fontWeight: 800,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          padding: "0.18rem 0.4rem",
          borderRadius: 999,
          background: active ? (activeColor ?? NGI_BLUE) + "2b" : "rgba(255,255,255,0.08)",
          border: `1px solid ${active ? (activeColor ?? NGI_BLUE) + "4a" : "rgba(255,255,255,0.1)"}`,
          color: active ? "#f6f8fb" : "rgba(247,248,251,0.6)",
        }}
      >
        {expanded !== undefined ? (expanded ? "▾" : "▸") : (active ? "On" : "Off")}
      </span>
    </button>
  );
}

function ViewToolbarButton({
  active,
  activeColor,
  title,
  onClick,
  children,
}: {
  active: boolean;
  activeColor?: string;
  title: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  const accent = activeColor ?? NGI_BLUE;
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      aria-pressed={active}
      style={{
        width: 40,
        height: 40,
        display: "grid",
        placeItems: "center",
        padding: 0,
        borderRadius: 12,
        cursor: "pointer",
        color: HUD_TEXT,
        background: active ? accent + "33" : "rgba(15,23,38,0.55)",
        border: `1px solid ${active ? accent + "88" : "rgba(255,255,255,0.18)"}`,
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        boxShadow: "0 6px 18px rgba(0,0,0,0.35)",
        transition: "background 0.12s ease, border-color 0.12s ease",
      }}
    >
      {children}
    </button>
  );
}

/** Eye + curved side arrows in opposing directions — first-person "look around" */
function IconLookAround() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {/* eye */}
      <path d="M5 12s2.8-4.8 7-4.8 7 4.8 7 4.8-2.8 4.8-7 4.8S5 12 5 12z" />
      <circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" />
      {/* left curved arrow — points UP (head end at top) */}
      <path d="M2.5 16 Q-0.5 12 2.5 8" opacity="0.9" />
      <polyline points="1 9.8 2.5 8 4.2 9.4" opacity="0.9" />
      {/* right curved arrow — points DOWN (head end at bottom) */}
      <path d="M21.5 8 Q24.5 12 21.5 16" opacity="0.9" />
      <polyline points="19.8 14.6 21.5 16 23 14.2" opacity="0.9" />
    </svg>
  );
}

/** Gyroscope — nested gimbal rings around a center, evokes free 3D rotation */
function IconOrbit() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {/* outer ring (vertical) */}
      <ellipse cx="12" cy="12" rx="3.5" ry="9" />
      {/* middle ring (horizontal) */}
      <ellipse cx="12" cy="12" rx="9" ry="3.5" />
      {/* inner sphere outline */}
      <circle cx="12" cy="12" r="3" />
      {/* center pivot dot */}
      <circle cx="12" cy="12" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Small camera glyph used as the "View controls" section header */
function IconCameraBadge() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
      <circle cx="12" cy="13.5" r="3.2" />
    </svg>
  );
}

/** Pencil/line icon for Draw Discontinuity */
function IconDrawDiscontinuity() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {/* pencil body */}
      <path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  );
}

/** Compass/angle icon for Measure Dip/Dir */
function IconMeasureDipDir() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {/* protractor arc */}
      <path d="M12 22 A10 10 0 0 1 12 2" />
      <path d="M12 22 A10 10 0 0 0 12 2" />
      {/* baseline */}
      <line x1="2" y1="12" x2="22" y2="12" />
      {/* needle */}
      <line x1="12" y1="12" x2="18" y2="5" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Camera icon for Take Photo */
function IconTakePhoto() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
      <circle cx="12" cy="13.5" r="3.2" />
    </svg>
  );
}

/** Floating action bar button for field capture tools */
function ActionBarButton({
  active,
  activeColor,
  label,
  onClick,
  children,
}: {
  active: boolean;
  activeColor: string;
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
        padding: "8px 16px",
        borderRadius: 14,
        cursor: "pointer",
        color: active ? "#fff" : "rgba(247,248,251,0.85)",
        background: active ? activeColor + "33" : "rgba(255,255,255,0.06)",
        border: `1.5px solid ${active ? activeColor + "99" : "rgba(255,255,255,0.15)"}`,
        boxShadow: active ? `0 0 12px ${activeColor}44` : "none",
        transition: "all 0.15s ease",
        minWidth: 72,
      }}
    >
      {children}
      <span style={{
        fontSize: "0.62rem",
        fontWeight: 700,
        letterSpacing: "0.03em",
        whiteSpace: "nowrap",
        opacity: active ? 1 : 0.7,
      }}>
        {label}
      </span>
    </button>
  );
}

/** Tunnel reticle with inward-pointing arrows converging on the center */
function IconRecalibrate() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {/* tunnel outline: full-bleed, taller floor for arrow breathing room */}
      <path d="M1 23 L1 12 A11 11 0 0 1 23 12 L23 23 Z" />
      {/* center dot (target) — sits lower so vertical arrows have equal room */}
      <circle cx="12" cy="15" r="1.6" fill="currentColor" stroke="none" />
      {/* top arrow (sits inside the arch crown) */}
      <line x1="12" y1="3" x2="12" y2="11" />
      <polyline points="10 9 12 11 14 9" />
      {/* bottom arrow (rises from the floor) */}
      <line x1="12" y1="22" x2="12" y2="18" />
      <polyline points="10 20 12 18 14 20" />
      {/* left arrow */}
      <line x1="3" y1="15" x2="9" y2="15" />
      <polyline points="7 13 9 15 7 17" />
      {/* right arrow */}
      <line x1="21" y1="15" x2="15" y2="15" />
      <polyline points="17 13 15 15 17 17" />
    </svg>
  );
}


interface AppProps {
  data: CenterlineData | null;
  profileAssignments: ProfileAssignment[];
  onNavigateUtbrettsprofil: () => void;
  onNavigateDashboard?: () => void;
}

export function App({ data, profileAssignments, onNavigateUtbrettsprofil: _onNavigateUtbrettsprofil, onNavigateDashboard }: AppProps) {
  const project = useProject();
  const cameraKey = storageKey(project.id, "camera");
  const [station, setStation] = useState(() => {
    const saved = localStorage.getItem(cameraKey);
    if (saved) try { return JSON.parse(saved).station ?? 0; } catch { /* ignore */ }
    return 0;
  });
  const [ctrlHeld, setCtrlHeld] = useState(false);
  const cameraQuatRef = useRef(new THREE.Quaternion());

  // Centerline lock mode state
  const [centerlineLock, setCenterlineLock] = useState(false);
  const [drawMode, setDrawMode] = useState(false);
  const [drawToolType, setDrawToolType] = useState<DrawToolType>("discontinuity");
  const [drawPickerOpen, setDrawPickerOpen] = useState(false);
  const [drawPickerHover, setDrawPickerHover] = useState<DrawToolType | null>(null);
  const drawPickerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const drawToolRefs = useRef<Map<DrawToolType, HTMLButtonElement>>(new Map());
  const [brushScale, setBrushScale] = useState(1.0);
  const [measureMode, setMeasureMode] = useState(false);
  const [clipPlaneActive, setClipPlaneActive] = useState(() => {
    try { return localStorage.getItem(storageKey(project.id, "clipPlaneActive")) === "true"; } catch { return false; }
  });
  const [clipPlaneFlipped, setClipPlaneFlipped] = useState(() => {
    try { return localStorage.getItem(storageKey(project.id, "clipPlaneFlipped")) === "true"; } catch { return false; }
  });
  const [clipPlaneMatrix, setClipPlaneMatrix] = useState<number[] | undefined>(() => {
    try {
      const saved = localStorage.getItem(storageKey(project.id, "clipPlaneMatrix"));
      if (saved) { const v = JSON.parse(saved); if (Array.isArray(v) && v.length === 16) return v; }
    } catch {}
    return undefined;
  });
  const drawRef = useRef<TunnelDraw3DHandle>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [photos, setPhotos] = useState<TunnelPhoto[]>([]);
  const photoCaptureRef = useRef<PhotoCaptureHandle>(null);
  const [arOverlay, setArOverlay] = useState(false);
  const arVideoRef = useRef<HTMLVideoElement>(null);
  const arStreamRef = useRef<MediaStream | null>(null);
  const [photoFlash, setPhotoFlash] = useState(false);
  const zoomFovRef = useRef(75); // pinch-zoom FOV for look-around mode (20-75)
  const [videoZoomScale, setVideoZoomScale] = useState(1); // video CSS scale to match FOV zoom
  const [hasOrientationSensor, setHasOrientationSensor] = useState(false);
  const [gyroEnabled, setGyroEnabled] = useState(false);
  const [enabledScans, setEnabledScans] = useState<Set<number>>(() => {
    try {
      const saved = localStorage.getItem(storageKey(project.id, "enabledScans"));
      if (saved) {
        const indices: number[] = JSON.parse(saved);
        // Filter out hidden endpoints (analysis results) — they should only be enabled via the analysis toggle
        return new Set(indices.filter(i => !project.pointcloudEndpoints[i]?.hidden));
      }
    } catch {}
    return new Set();
  });
  const [pcExpanded, setPcExpanded] = useState(false);
  const pcCache = usePointcloudCacheStatus();
  const [pcLoadingIndices, setPcLoadingIndices] = useState<Set<number>>(new Set());
   const [showColtopOverlay, setShowColtopOverlay] = useState(false);
   const [stereonetSelection, setStereonetSelection] = useState<{ x: number; y: number; r: number } | null>(null);
   const stereonetDragRef = useRef<{ startX: number; startY: number } | null>(null);
   const [enabledAnalyses, setEnabledAnalyses] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem(storageKey(project.id, "enabledAnalyses"));
      if (saved) return new Set(JSON.parse(saved));
    } catch {}
    return new Set();
  });
   const stereonetFilterIndex = useMemo(() => {
     for (const ep of project.pointcloudEndpoints) {
       if (!ep.analyses) continue;
       for (const a of ep.analyses) {
         if (enabledAnalyses.has(a.id) && a.resultPointcloudIndex != null) {
           return a.resultPointcloudIndex;
         }
       }
     }
     return undefined;
   }, [enabledAnalyses, project.pointcloudEndpoints]);
  // On mount, sync analysis result pointclouds into enabledScans
  useEffect(() => {
    if (enabledAnalyses.size === 0) return;
    const toAdd: number[] = [];
    for (const ep of project.pointcloudEndpoints) {
      if (!ep.analyses) continue;
      for (const a of ep.analyses) {
        if (enabledAnalyses.has(a.id) && a.resultPointcloudIndex != null) {
          toAdd.push(a.resultPointcloudIndex);
        }
      }
    }
    if (toAdd.length > 0) {
      setEnabledScans((prev) => {
        const next = new Set(prev);
        for (const idx of toAdd) next.add(idx);
        return next;
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [expandedScans, setExpandedScans] = useState<Set<number>>(new Set());
  const [salvoData, setSalvoData] = useState<SalvoJsonData | null>(null);
  const [salvoExpanded, setSalvoExpanded] = useState(() => {
    try { return localStorage.getItem(storageKey(project.id, "salvoExpanded")) === "true"; } catch { return false; }
  });
  const [dataTablesExpanded, setDataTablesExpanded] = useState(() => {
    try { return localStorage.getItem(storageKey(project.id, "dataTablesExpanded")) === "true"; } catch { return false; }
  });
  const [geoModelExpanded, setGeoModelExpanded] = useState(false);
  const [geoModelCreating, setGeoModelCreating] = useState(false);
  const [enabledSalvos, setEnabledSalvos] = useState<Set<number>>(() => {
    try {
      const saved = localStorage.getItem(storageKey(project.id, "enabledSalvos"));
      if (saved) return new Set(JSON.parse(saved));
    } catch {}
    return new Set();
  });
  // Per-salvo data table status: Map<scanNumber, Map<tableId, status>>
  const [salvoTableStatus, setSalvoTableStatus] = useState<Map<number, Map<string, DataTableStatus>>>(new Map());
  // Which table+column is currently selected for visualization (shared settings)
  const [activeTableId, setActiveTableId] = useState<string | null>(() => {
    try { return localStorage.getItem(storageKey(project.id, "activeTableId")) || null; } catch { return null; }
  });
  const [activeColumnName, setActiveColumnName] = useState<string | null>(() => {
    try { return localStorage.getItem(storageKey(project.id, "activeColumnName")) || null; } catch { return null; }
  });
  // Disabled categories for categorical columns (hidden from rendering)
  const [disabledCategories, setDisabledCategories] = useState<Set<string>>(new Set());
  const [mwdRawData, setMwdRawData] = useState<Map<number, MwdRawScanData>>(new Map());
  // Generic table data: Map<tableId, Map<scanNumber, MwdRawScanData>>
  const [extraTableData, setExtraTableData] = useState<Map<string, Map<number, MwdRawScanData>>>(new Map());
  const [mwdRawSensor, setMwdRawSensor] = useState<MwdSensorName | null>(() => {
    try {
      const saved = localStorage.getItem(storageKey(project.id, "mwdRawSensor"));
      return saved ? saved as MwdSensorName : null;
    } catch { return null; }
  });
  const [mwdRawClamp, setMwdRawClamp] = useState<[number, number]>(() => {
    try {
      const saved = localStorage.getItem(storageKey(project.id, "mwdRawClamp"));
      if (saved) { const v = JSON.parse(saved); if (Array.isArray(v) && v.length === 2) return v as [number, number]; }
    } catch {}
    return [0, 100];
  });
  const [showMwdRaw, setShowMwdRaw] = useState(() => {
    try { return localStorage.getItem(storageKey(project.id, "showMwdRaw")) === "true"; } catch { return false; }
  });
  const [showMWD, setShowMWD] = useState(() => {
    try { return localStorage.getItem(storageKey(project.id, "showMWD")) === "true"; } catch { return false; }
  });
  const [mwdOpacity, setMwdOpacity] = useState(0.85);
  const [rbfGeometry, setRbfGeometry] = useState<THREE.BufferGeometry | null>(null);
  const [rbfGrid, setRbfGrid] = useState<ScalarGrid | null>(null);
  const [rbfStatus, setRbfStatus] = useState<string | null>(null);
  const [rbfRadius, setRbfRadius] = useState(6);
  const [rbfFlatness, setRbfFlatness] = useState(5);
  const [rbfResolution, setRbfResolution] = useState(25);
  const [rbfIsoValue, setRbfIsoValue] = useState(0.5);
  const [rbfOpacity, setRbfOpacity] = useState(50);
  const [rbfSavedLayers, setRbfSavedLayers] = useState<RBFLayer[]>([]);
  const [rbfSaveName, setRbfSaveName] = useState("");
  const rbfLayerIdRef = useRef(0);
  const [rbfDrawnNormal, setRbfDrawnNormal] = useState<[number, number, number] | null>(null);
  const [rbfDrawingPlane, setRbfDrawingPlane] = useState(false);
  const [rbfDrawLine, setRbfDrawLine] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
  const [rbfSelectedCategories, setRbfSelectedCategories] = useState<Set<string>>(new Set());
  const [rbfPendingCatLayers, setRbfPendingCatLayers] = useState<{ name: string; grid: ScalarGrid; geometry: THREE.BufferGeometry; color: string }[] | null>(null);
  const [mwdLegend, setMwdLegend] = useState<{ rockType: string; color: string }[]>([]);
  const [sidebarVisible, setSidebarVisible] = useState(() => {
    try { return localStorage.getItem(storageKey(project.id, "sidebarVisible")) === "true"; } catch { return false; }
  });
  const deviceOrientationRef = useRef<{ alpha: number; beta: number; gamma: number } | null>(null);
  const mouseLookRef = useRef<{ yaw: number; pitch: number } | null>(null);
  const [orientationPermissionNeeded, setOrientationPermissionNeeded] = useState(false);
  const shouldRecalibrate = useRef(false);

  // Load photos from IndexedDB (async), with migration from old localStorage format
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Try migration first
      const migrated = await migrateFromLocalStorage(project.id);
      if (migrated.length > 0 && !cancelled) {
        setPhotos(migrated);
        return;
      }
      // Normal load
      const loaded = await loadAllPhotos(project.id);
      if (!cancelled) setPhotos(loaded);
    })();
    return () => { cancelled = true; };
  }, []);

  // Load saved RBF interpretation layers from IndexedDB
  useEffect(() => {
    let cancelled = false;
    loadRBFLayers(project.id).then((layers) => {
      if (!cancelled) {
        setRbfSavedLayers(layers);
        rbfLayerIdRef.current = layers.reduce((max, l) => Math.max(max, l.id), 0);
      }
    });
    return () => { cancelled = true; };
  }, [project.id]);

  // Fetch MWD legend data when project has mwdEndpoint
  useEffect(() => {
    if (!project.mwdEndpoint) { setMwdLegend([]); return; }
    fetch(project.mwdEndpoint)
      .then((r) => r.json())
      .then((d: MWDData) => setMwdLegend(getMWDRockTypes(d)))
      .catch(() => setMwdLegend([]));
  }, [project.mwdEndpoint]);

  // Persist viewer state to localStorage
  useEffect(() => {
    try { localStorage.setItem(storageKey(project.id, "enabledScans"), JSON.stringify([...enabledScans])); } catch {}
  }, [enabledScans, project.id]);
  useEffect(() => {
    try { localStorage.setItem(storageKey(project.id, "enabledAnalyses"), JSON.stringify([...enabledAnalyses])); } catch {}
  }, [enabledAnalyses, project.id]);
  useEffect(() => {
    try { localStorage.setItem(storageKey(project.id, "enabledSalvos"), JSON.stringify([...enabledSalvos])); } catch {}
  }, [enabledSalvos, project.id]);
  useEffect(() => {
    try { localStorage.setItem(storageKey(project.id, "showMwdRaw"), String(showMwdRaw)); } catch {}
  }, [showMwdRaw, project.id]);
  useEffect(() => {
    try { localStorage.setItem(storageKey(project.id, "showMWD"), String(showMWD)); } catch {}
  }, [showMWD, project.id]);
  useEffect(() => {
    try { localStorage.setItem(storageKey(project.id, "sidebarVisible"), String(sidebarVisible)); } catch {}
  }, [sidebarVisible, project.id]);
  useEffect(() => {
    try {
      if (activeTableId) localStorage.setItem(storageKey(project.id, "activeTableId"), activeTableId);
      else localStorage.removeItem(storageKey(project.id, "activeTableId"));
    } catch {}
  }, [activeTableId, project.id]);
  useEffect(() => {
    try {
      if (activeColumnName) localStorage.setItem(storageKey(project.id, "activeColumnName"), activeColumnName);
      else localStorage.removeItem(storageKey(project.id, "activeColumnName"));
    } catch {}
  }, [activeColumnName, project.id]);
  useEffect(() => {
    try {
      if (mwdRawSensor) localStorage.setItem(storageKey(project.id, "mwdRawSensor"), mwdRawSensor);
      else localStorage.removeItem(storageKey(project.id, "mwdRawSensor"));
    } catch {}
  }, [mwdRawSensor, project.id]);
  useEffect(() => {
    try { localStorage.setItem(storageKey(project.id, "salvoExpanded"), String(salvoExpanded)); } catch {}
  }, [salvoExpanded, project.id]);
  useEffect(() => {
    try { localStorage.setItem(storageKey(project.id, "dataTablesExpanded"), String(dataTablesExpanded)); } catch {}
  }, [dataTablesExpanded, project.id]);
  useEffect(() => {
    try { localStorage.setItem(storageKey(project.id, "mwdRawClamp"), JSON.stringify(mwdRawClamp)); } catch {}
  }, [mwdRawClamp, project.id]);
  useEffect(() => {
    try { localStorage.setItem(storageKey(project.id, "clipPlaneActive"), String(clipPlaneActive)); } catch {}
  }, [clipPlaneActive, project.id]);
  useEffect(() => {
    try { localStorage.setItem(storageKey(project.id, "clipPlaneFlipped"), String(clipPlaneFlipped)); } catch {}
  }, [clipPlaneFlipped, project.id]);
  useEffect(() => {
    try {
      if (clipPlaneMatrix) localStorage.setItem(storageKey(project.id, "clipPlaneMatrix"), JSON.stringify(clipPlaneMatrix));
      else localStorage.removeItem(storageKey(project.id, "clipPlaneMatrix"));
    } catch {}
  }, [clipPlaneMatrix, project.id]);

  // Fetch salvo data when project has salvoEndpoint
  useEffect(() => {
    if (!project.salvoEndpoint) { setSalvoData(null); setEnabledSalvos(new Set()); return; }
    fetch(project.salvoEndpoint)
      .then((r) => r.json())
      .then((d: SalvoJsonData) => {
        setSalvoData(d);
        // Keep persisted enabledSalvos — only filter out scans that don't exist in the data
        setEnabledSalvos((prev) => {
          if (prev.size === 0) return prev;
          const validScans = new Set(d.salvos.map((s) => s.scan));
          const filtered = new Set([...prev].filter((s) => validScans.has(s)));
          return filtered.size === prev.size ? prev : filtered;
        });
      })
      .catch(() => { setSalvoData(null); setEnabledSalvos(new Set()); });
  }, [project.salvoEndpoint]);

  // Fetch MWD raw data for enabled salvos and update table status
  useEffect(() => {
    if (!enabledSalvos.size || !project.dataTableSchemas?.length) return;
    const mwdRawSchema = project.dataTableSchemas.find((s) => s.id === "mwd_raw");
    if (!mwdRawSchema) return;
    let cancelled = false;
    const toLoad = [...enabledSalvos].filter((s) => !mwdRawData.has(s));
    if (!toLoad.length) return;
    // Mark loading status
    setSalvoTableStatus((prev) => {
      const next = new Map(prev);
      for (const scan of toLoad) {
        const tableMap = new Map(next.get(scan) ?? []);
        tableMap.set("mwd_raw", "loading");
        next.set(scan, tableMap);
      }
      return next;
    });
    Promise.all(
      toLoad.map((scan) => {
        const url = scan >= 100
          ? `/api/mwd-raw/injection-screen-${scan - 100}`
          : mwdRawSchema.endpointPattern.replace("{scan}", String(scan));
        return fetch(url)
          .then((r) => r.ok ? r.json() : Promise.reject(new Error("not found")))
          .then((d: MwdRawScanData) => [scan, d, "available"] as const)
          .catch(() => [scan, null, "not_applicable"] as const);
      })
    ).then((results) => {
      if (cancelled) return;
      setMwdRawData((prev) => {
        const next = new Map(prev);
        for (const r of results) {
          if (r[1]) next.set(r[0], r[1]);
        }
        return next;
      });
      setSalvoTableStatus((prev) => {
        const next = new Map(prev);
        for (const [scan, , status] of results) {
          const tableMap = new Map(next.get(scan) ?? []);
          tableMap.set("mwd_raw", status as DataTableStatus);
          next.set(scan, tableMap);
        }
        return next;
      });
    });
    return () => { cancelled = true; };
  }, [enabledSalvos, project.dataTableSchemas]);

  // Fetch data for non-mwd_raw table schemas (e.g. mwd_ml)
  useEffect(() => {
    if (!enabledSalvos.size || !project.dataTableSchemas?.length) return;
    const extraSchemas = project.dataTableSchemas.filter((s) => s.id !== "mwd_raw" && s.id !== "mwd_interp");
    if (!extraSchemas.length) return;
    let cancelled = false;

    for (const schema of extraSchemas) {
      const existing = extraTableData.get(schema.id);
      const toLoad = [...enabledSalvos].filter((s) => !existing?.has(s));
      if (!toLoad.length) continue;

      // Mark loading status
      setSalvoTableStatus((prev) => {
        const next = new Map(prev);
        for (const scan of toLoad) {
          const tableMap = new Map(next.get(scan) ?? []);
          tableMap.set(schema.id, "loading");
          next.set(scan, tableMap);
        }
        return next;
      });

      Promise.all(
        toLoad.map((scan) => {
          const url = scan >= 100
            ? schema.endpointPattern.replace(/scan-\{scan\}/, `injection-screen-${scan - 100}`)
            : schema.endpointPattern.replace("{scan}", String(scan));
          return fetch(url)
            .then((r) => r.ok ? r.json() : Promise.reject(new Error("not found")))
            .then((d: MwdRawScanData) => [scan, d, "available"] as const)
            .catch(() => [scan, null, "not_applicable"] as const);
        })
      ).then((results) => {
        if (cancelled) return;
        setExtraTableData((prev) => {
          const next = new Map(prev);
          const tableMap = new Map(next.get(schema.id) ?? []);
          for (const [scan, data] of results) {
            if (data) tableMap.set(scan, data);
          }
          next.set(schema.id, tableMap);
          return next;
        });
        setSalvoTableStatus((prev) => {
          const next = new Map(prev);
          for (const [scan, , status] of results) {
            const tableMap = new Map(next.get(scan) ?? []);
            tableMap.set(schema.id, status as DataTableStatus);
            next.set(scan, tableMap);
          }
          return next;
        });
      });
    }

    return () => { cancelled = true; };
  }, [enabledSalvos, project.dataTableSchemas]);

  // Detect orientation sensor support
  useEffect(() => {
    if (typeof DeviceOrientationEvent !== "undefined") {
      // Check if permission API exists (iOS 13+)
      if (typeof (DeviceOrientationEvent as any).requestPermission === "function") {
        setHasOrientationSensor(true);
        setOrientationPermissionNeeded(true);
      } else {
        // Android / other — test if events fire.
        // Use touch heuristic: any device with a coarse pointer (touchscreen)
        // is very likely a mobile device with gyroscope.
        let detected = false;
        const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;

        const testHandler = (e: DeviceOrientationEvent) => {
          if (e.alpha !== null && !detected) {
            detected = true;
            setHasOrientationSensor(true);
            window.removeEventListener("deviceorientation", testHandler);
          }
        };
        window.addEventListener("deviceorientation", testHandler);

        if (isTouchDevice) {
          // Optimistically assume sensor exists on touch devices — never revoke.
          // deviceorientation events can take a long time to start firing
          // (sensor wake, browser throttling, etc.) so timeout-based detection
          // is inherently unreliable. The worst case is a desktop with a touch
          // monitor where gyro look-around simply won't respond — those users
          // have a mouse anyway.
          setHasOrientationSensor(true);
          // Keep listener alive to confirm, but don't revoke on timeout
          return () => {
            window.removeEventListener("deviceorientation", testHandler);
          };
        } else {
          // Desktop — clean up after 3s if no event fires
          const timeout = setTimeout(() => {
            window.removeEventListener("deviceorientation", testHandler);
          }, 3000);
          return () => {
            clearTimeout(timeout);
            window.removeEventListener("deviceorientation", testHandler);
          };
        }
      }
    }
  }, []);

  // Listen to device orientation when lock mode is active AND gyro is enabled
  useEffect(() => {
    if (!centerlineLock || !gyroEnabled) {
      deviceOrientationRef.current = null;
      return;
    }
    if (!hasOrientationSensor) return; // drag-look effect handles this case
    const handler = (e: DeviceOrientationEvent) => {
      if (e.alpha !== null && e.beta !== null && e.gamma !== null) {
        deviceOrientationRef.current = { alpha: e.alpha, beta: e.beta, gamma: e.gamma };
      }
    };
    window.addEventListener("deviceorientation", handler);
    return () => window.removeEventListener("deviceorientation", handler);
  }, [centerlineLock, hasOrientationSensor, gyroEnabled]);

  // Manual look (click-drag): when in lock mode and gyro is not active,
  // use left-click drag to drive yaw/pitch.
  useEffect(() => {
    if (!centerlineLock || drawMode || (hasOrientationSensor && gyroEnabled)) {
      // When leaving lock mode or switching to gyro, clear the look state.
      // When entering draw mode, keep the current yaw/pitch so the camera
      // holds its orientation — just don't register drag listeners.
      if (!centerlineLock || (hasOrientationSensor && gyroEnabled)) {
        mouseLookRef.current = null;
      }
      return;
    }

    // Initialize accumulators only if not already set (preserves orientation
    // when returning from draw mode).
    if (!mouseLookRef.current) {
      mouseLookRef.current = { yaw: 0, pitch: 0 };
    }

    const canvas = document.querySelector("canvas");
    const SENSITIVITY = 0.0025;
    const MAX_PITCH = Math.PI / 2 - 0.01;
    let lastX = 0;
    let lastY = 0;
    let dragging = false;

    const onMouseDown = (e: MouseEvent) => {
      // Left button only (button 0)
      if (e.button !== 0) return;
      lastX = e.clientX;
      lastY = e.clientY;
      dragging = true;
    };

    const onMove = (e: MouseEvent) => {
      const m = mouseLookRef.current;
      if (!m || !dragging) return;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      m.yaw += dx * SENSITIVITY;
      m.pitch += dy * SENSITIVITY;
      if (m.pitch > MAX_PITCH) m.pitch = MAX_PITCH;
      if (m.pitch < -MAX_PITCH) m.pitch = -MAX_PITCH;
    };

    const onMouseUp = () => {
      dragging = false;
    };

    // ── Mobile: 1-finger drag to rotate camera ──
    const TOUCH_SENSITIVITY = 0.004;
    let touchId: number | null = null;
    let lastTouchX = 0;
    let lastTouchY = 0;

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) { touchId = null; return; }
      const t = e.touches[0];
      touchId = t.identifier;
      lastTouchX = t.clientX;
      lastTouchY = t.clientY;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (touchId === null || e.touches.length !== 1) return;
      const t = e.touches[0];
      if (t.identifier !== touchId) return;
      const m = mouseLookRef.current;
      if (!m) return;
      e.preventDefault();
      const dx = t.clientX - lastTouchX;
      const dy = t.clientY - lastTouchY;
      lastTouchX = t.clientX;
      lastTouchY = t.clientY;
      m.yaw += dx * TOUCH_SENSITIVITY;
      m.pitch += dy * TOUCH_SENSITIVITY;
      if (m.pitch > MAX_PITCH) m.pitch = MAX_PITCH;
      if (m.pitch < -MAX_PITCH) m.pitch = -MAX_PITCH;
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (touchId === null) return;
      let found = false;
      for (let i = 0; i < e.touches.length; i++) {
        if (e.touches[i].identifier === touchId) { found = true; break; }
      }
      if (!found) touchId = null;
    };

    canvas?.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onMouseUp);
    canvas?.addEventListener("touchstart", onTouchStart, { passive: true });
    canvas?.addEventListener("touchmove", onTouchMove, { passive: false });
    canvas?.addEventListener("touchend", onTouchEnd, { passive: true });

    return () => {
      canvas?.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onMouseUp);
      canvas?.removeEventListener("touchstart", onTouchStart);
      canvas?.removeEventListener("touchmove", onTouchMove);
      canvas?.removeEventListener("touchend", onTouchEnd);
      // Don't null mouseLookRef here — the new effect run decides whether to keep or clear it
    };
  }, [centerlineLock, drawMode, hasOrientationSensor, gyroEnabled]);

  // Live camera feed for AR overlay — auto-start on mobile when entering look-around
  useEffect(() => {
    const shouldStream = centerlineLock && arOverlay;
    if (!shouldStream) {
      // Stop stream
      if (arStreamRef.current) {
        arStreamRef.current.getTracks().forEach((t) => t.stop());
        arStreamRef.current = null;
      }
      if (arVideoRef.current) arVideoRef.current.srcObject = null;
      return;
    }

    let cancelled = false;
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "environment", width: { ideal: 3840 }, height: { ideal: 2160 } }, audio: false })
      .then((stream) => {
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        arStreamRef.current = stream;
        if (arVideoRef.current) {
          arVideoRef.current.srcObject = stream;
          arVideoRef.current.play().catch(() => {});
        }
      })
      .catch((err) => {
        console.warn("Camera access denied or unavailable:", err);
        setArOverlay(false);
      });

    return () => {
      cancelled = true;
      if (arStreamRef.current) {
        arStreamRef.current.getTracks().forEach((t) => t.stop());
        arStreamRef.current = null;
      }
    };
  }, [arOverlay, centerlineLock]);

  // Turn off AR overlay when leaving look-around mode; reset zoom
  useEffect(() => {
    if (!centerlineLock) {
      setArOverlay(false);
      zoomFovRef.current = 75;
      setVideoZoomScale(1);
    }
  }, [centerlineLock]);

  // Pinch-to-zoom in look-around mode — changes camera FOV
  useEffect(() => {
    if (!centerlineLock) return;
    let lastPinchDist = 0;
    let pinchStartFov = 75;

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        lastPinchDist = Math.sqrt(dx * dx + dy * dy);
        pinchStartFov = zoomFovRef.current;
      }
    };
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 2) return;
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (lastPinchDist > 0) {
        // Pinch out (spread) = zoom in = lower FOV
        const ratio = lastPinchDist / dist;
        const newFov = Math.max(20, Math.min(75, pinchStartFov * ratio));
        zoomFovRef.current = newFov;
        // Update video scale to match: scale = baseFov / currentFov
        setVideoZoomScale(75 / newFov);
      }
    };
    const onTouchEnd = () => {
      lastPinchDist = 0;
    };

    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: true });
    document.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
    };
  }, [centerlineLock]);

  // Toggle handler (with iOS permission request when applicable)
  const handleToggleLock = async () => {
    if (centerlineLock) {
      setCenterlineLock(false);
      return;
    }
    if (hasOrientationSensor && orientationPermissionNeeded) {
      try {
        const result = await (DeviceOrientationEvent as any).requestPermission();
        if (result !== "granted") return;
        setOrientationPermissionNeeded(false);
      } catch {
        return;
      }
    }
    setCenterlineLock(true);
  };

  // Mutually exclusive tool modes
  const handleToggleDraw = () => {
    setDrawMode((d) => {
      if (!d) setMeasureMode(false);
      return !d;
    });
  };
  const handleDrawPointerDown = () => {
    drawPickerTimer.current = setTimeout(() => {
      setDrawPickerOpen(true);
      drawPickerTimer.current = null;
    }, 400);
  };
  const handleDrawPointerUp = () => {
    if (drawPickerTimer.current) {
      clearTimeout(drawPickerTimer.current);
      drawPickerTimer.current = null;
      // Short press — toggle draw mode with current tool
      handleToggleDraw();
    }
    // Long-press release is handled by the global pointerup listener
  };
  const handleSelectDrawTool = (tool: DrawToolType) => {
    setDrawToolType(tool);
    setDrawPickerOpen(false);
    setDrawPickerHover(null);
    setDrawMode(true);
    setMeasureMode(false);
  };
  const handleToggleMeasure = () => {
    setMeasureMode((m) => {
      if (!m) setDrawMode(false); // deactivate draw when activating measure
      return !m;
    });
  };
  const handleTakePhoto = () => {
    if (arStreamRef.current && arVideoRef.current && arVideoRef.current.videoWidth > 0) {
      // Capture frame from live video feed
      captureFrameFromVideo();
    } else {
      // Fallback: file picker
      photoInputRef.current?.click();
    }
  };

  const captureFrameFromVideo = async () => {
    const video = arVideoRef.current;
    if (!video || !data) return;

    // Raycast center-screen to find where on the tunnel surface we're looking
    const hit = photoCaptureRef.current?.getHit();
    if (!hit) {
      console.warn("No tunnel surface hit — aim at the tunnel wall");
      return;
    }

    // Flash feedback immediately so user knows capture happened
    setPhotoFlash(true);
    setTimeout(() => setPhotoFlash(false), 200);

    let dataUrl: string;

    // Try ImageCapture API for full-resolution still (hardware-quality photo)
    const track = arStreamRef.current?.getVideoTracks()[0];
    if (track && typeof ImageCapture !== "undefined") {
      try {
        const imageCapture = new ImageCapture(track);
        const blob = await imageCapture.takePhoto();
        dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch {
        // Fallback to canvas grab if ImageCapture fails
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth || 1920;
        canvas.height = video.videoHeight || 1080;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        dataUrl = canvas.toDataURL("image/jpeg", 0.92);
      }
    } else {
      // Fallback: grab frame from video element
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth || 1920;
      canvas.height = video.videoHeight || 1080;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    }

    // Convert hit point to tunnel coordinates for station display
    const frames = data.curve.computeFrenetFrames(2000, false);
    const curvePoints = data.curve.getSpacedPoints(2000);
    const hitTunnel = worldToTunnelPoint3D(hit.point, data, frames, curvePoints, 2000);

    const fovRad = THREE.MathUtils.degToRad(hit.fov);
    const visibleHeight = 2 * hit.distance * Math.tan(fovRad / 2);
    const decalSize = Math.max(1, Math.min(12, visibleHeight));

    const photo: TunnelPhoto = {
      id: crypto.randomUUID(),
      station: hitTunnel.station,
      hitU: hitTunnel.u,
      hitV: hitTunnel.v,
      hitX: hit.point.x,
      hitY: hit.point.y,
      hitZ: hit.point.z,
      dirX: hit.dir.x,
      dirY: hit.dir.y,
      dirZ: hit.dir.z,
      size: decalSize,
      projViewMatrix: hit.projViewMatrix,
      camX: hit.camPos.x,
      camY: hit.camPos.y,
      camZ: hit.camPos.z,
      dataUrl,
      createdAt: Date.now(),
    };

    const updated = [...photos, photo];
    setPhotos(updated);
    savePhotoMetas(project.id, updated.map(({ dataUrl: _, ...m }) => m));
    savePhotoImage(project.id, photo.id, dataUrl).catch((err) => console.warn("Failed to save photo image:", err));
    console.log(`Photo captured at station ${hitTunnel.station.toFixed(0)}m (${video.videoWidth}x${video.videoHeight})`);
  };
  const handlePhotoCapture = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !data) return;

    // Raycast center-screen to find tunnel surface hit
    const hit = photoCaptureRef.current?.getHit();
    if (!hit) {
      console.warn("No tunnel surface hit — aim at the tunnel wall");
      e.target.value = "";
      return;
    }

    const frames = data.curve.computeFrenetFrames(2000, false);
    const curvePoints = data.curve.getSpacedPoints(2000);
    const hitTunnel = worldToTunnelPoint3D(hit.point, data, frames, curvePoints, 2000);

    try {
      const dataUrl = await fileToDataUrl(file);

      const fovRad = THREE.MathUtils.degToRad(hit.fov);
      const visibleHeight = 2 * hit.distance * Math.tan(fovRad / 2);
      const decalSize = Math.max(1, Math.min(12, visibleHeight));

      const photo: TunnelPhoto = {
        id: crypto.randomUUID(),
        station: hitTunnel.station,
        hitU: hitTunnel.u,
        hitV: hitTunnel.v,
        hitX: hit.point.x,
        hitY: hit.point.y,
        hitZ: hit.point.z,
        dirX: hit.dir.x,
        dirY: hit.dir.y,
        dirZ: hit.dir.z,
        size: decalSize,
        projViewMatrix: hit.projViewMatrix,
        camX: hit.camPos.x,
        camY: hit.camPos.y,
        camZ: hit.camPos.z,
        dataUrl,
        createdAt: Date.now(),
      };

      const updated = [...photos, photo];
      setPhotos(updated);
      savePhotoMetas(project.id, updated.map(({ dataUrl: _, ...m }) => m));
      await savePhotoImage(project.id, photo.id, dataUrl);
      console.log(`Photo placed at station ${hitTunnel.station.toFixed(0)}m`);
    } catch (err) {
      console.error("Failed to read photo file:", err);
    }

    // Reset input so the same file can be re-selected
    e.target.value = "";
  };

  const arPhotoActive = arOverlay && centerlineLock;

  const filteredSalvoData = useMemo<SalvoJsonData | null>(() => {
    if (!salvoData) return null;
    const filtered = salvoData.salvos.filter((s) => enabledSalvos.has(s.scan));
    return { salvos: filtered };
  }, [salvoData, enabledSalvos]);

  // Compute global min/max for the active MWD sensor (for sidebar legend)
  const mwdSensorRange = useMemo(() => {
    if (!mwdRawSensor || !mwdRawData.size) return null;
    let min = Infinity, max = -Infinity;
    for (const scanData of mwdRawData.values()) {
      for (const hole of scanData.holes) {
        const vals = hole.values[mwdRawSensor];
        if (!vals) continue;
        for (const v of vals) {
          if (typeof v === "number") {
            if (v < min) min = v;
            if (v > max) max = v;
          }
        }
      }
    }
    if (!isFinite(min) || !isFinite(max) || min === max) return null;
    return { min, max };
  }, [mwdRawSensor, mwdRawData]);

  // Clamped range based on slider percentages
  const mwdClampedRange = useMemo(() => {
    if (!mwdSensorRange) return null;
    const range = mwdSensorRange.max - mwdSensorRange.min;
    return {
      min: mwdSensorRange.min + (mwdRawClamp[0] / 100) * range,
      max: mwdSensorRange.min + (mwdRawClamp[1] / 100) * range,
    };
  }, [mwdSensorRange, mwdRawClamp]);

  // Determine effective borehole data/sensor/colors for rendering.
  // When an extra table (non-mwd_raw) is active with a column selected, use that table's data.
  const activeExtraSchema = useMemo(() => {
    if (!activeTableId || activeTableId === "mwd_raw" || activeTableId === "mwd_interp") return null;
    return project.dataTableSchemas?.find((s) => s.id === activeTableId) ?? null;
  }, [activeTableId, project.dataTableSchemas]);

  const activeExtraColumn = useMemo(() => {
    if (!activeExtraSchema || !activeColumnName) return null;
    return activeExtraSchema.columns.find((c) => c.name === activeColumnName) ?? null;
  }, [activeExtraSchema, activeColumnName]);

  const effectiveMwdData = useMemo((): Map<number, MwdRawScanData> | undefined => {
    if (activeExtraSchema && activeColumnName) {
      return extraTableData.get(activeExtraSchema.id);
    }
    return showMwdRaw ? mwdRawData : undefined;
  }, [activeExtraSchema, activeColumnName, extraTableData, showMwdRaw, mwdRawData]);

  const effectiveSensor = useMemo((): MwdSensorName | null => {
    if (activeExtraSchema && activeColumnName) {
      return activeColumnName as MwdSensorName;
    }
    return showMwdRaw ? mwdRawSensor : null;
  }, [activeExtraSchema, activeColumnName, showMwdRaw, mwdRawSensor]);

  const effectiveCategoryColors = useMemo((): CategoryColorMap | null => {
    if (!activeExtraColumn || activeExtraColumn.type !== "categorical" || !activeExtraColumn.categories) return null;
    const map: CategoryColorMap = new Map();
    for (const cat of activeExtraColumn.categories) {
      const hex = cat.color;
      const r = parseInt(hex.slice(1, 3), 16) / 255;
      const g = parseInt(hex.slice(3, 5), 16) / 255;
      const b = parseInt(hex.slice(5, 7), 16) / 255;
      map.set(cat.value, [r, g, b]);
    }
    return map;
  }, [activeExtraColumn]);

  const effectiveClampedRange = useMemo(() => {
    // No clamping for categorical columns or extra tables
    if (activeExtraSchema && activeColumnName) return null;
    return showMwdRaw ? mwdClampedRange : null;
  }, [activeExtraSchema, activeColumnName, showMwdRaw, mwdClampedRange]);

  useEffect(() => {
    const down = (e: KeyboardEvent) => { if (e.key === "Control") setCtrlHeld(true); };
    const up = (e: KeyboardEvent) => { if (e.key === "Control") setCtrlHeld(false); };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", () => setCtrlHeld(false));
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, []);

  // Track pointer over sub-tool buttons while picker is open; select on release
  const handleSelectDrawToolRef = useRef(handleSelectDrawTool);
  handleSelectDrawToolRef.current = handleSelectDrawTool;

  useEffect(() => {
    if (!drawPickerOpen) return;

    const hitTest = (e: PointerEvent): DrawToolType | null => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (!el) return null;
      for (const [tool, btn] of drawToolRefs.current) {
        if (btn && btn.contains(el as Node)) return tool;
      }
      return null;
    };

    const onMove = (e: PointerEvent) => {
      setDrawPickerHover(hitTest(e));
    };

    const onUp = (e: PointerEvent) => {
      const tool = hitTest(e);
      if (tool) {
        handleSelectDrawToolRef.current(tool);
      } else {
        setDrawPickerOpen(false);
        setDrawPickerHover(null);
      }
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [drawPickerOpen]);

  useEffect(() => {
    if (!data) return;
    const handleWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      const direction = e.deltaY > 0 ? -1 : 1;
      setStation((prev) => {
        const next = prev + direction * RING_SPACING;
        return Math.max(0, Math.min(data.totalLength, next));
      });
    };
    window.addEventListener("wheel", handleWheel, { passive: false });
    return () => window.removeEventListener("wheel", handleWheel);
  }, [data]);

  return (
    <div style={{
      width: "100vw",
      height: "100dvh",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      position: "relative",
      background: arPhotoActive ? "transparent" : `radial-gradient(circle at top left, rgba(171,35,40,0.18), transparent 24%), linear-gradient(135deg, ${NGI_BLUE} 0%, #08131f 58%, #11161f 100%)`,
      fontFamily: "system-ui",
      color: HUD_TEXT,
    }}>

      {/* Live camera feed background — AR overlay behind transparent 3D canvas */}
      <div style={{
        position: "absolute",
        inset: 0,
        zIndex: 0,
        background: "#000",
        overflow: "hidden",
        display: arPhotoActive ? "block" : "none",
      }}>
        <video
          ref={arVideoRef}
          autoPlay
          playsInline
          muted
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transform: `scale(${videoZoomScale})`,
            transformOrigin: "center center",
          }}
        />
      </div>

      {/* Zoom level indicator */}
      {arPhotoActive && videoZoomScale > 1.05 && (
        <div style={{
          position: "absolute",
          top: 12,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 10,
          background: "rgba(0,0,0,0.5)",
          color: "#fff",
          padding: "4px 12px",
          borderRadius: 12,
          fontSize: 13,
          fontWeight: 600,
          fontFamily: "system-ui, sans-serif",
          backdropFilter: "blur(4px)",
          pointerEvents: "none",
        }}>
          {videoZoomScale.toFixed(1)}x
        </div>
      )}

      {/* Photo capture flash */}
      {photoFlash && (
        <div style={{
          position: "absolute",
          inset: 0,
          zIndex: 10,
          background: "white",
          opacity: 0.7,
          pointerEvents: "none",
          transition: "opacity 0.15s ease-out",
        }} />
      )}

      {/* Canvas — fills full viewport, above video feed */}
      <div style={{ position: "absolute", inset: 0, zIndex: 1 }}>
        <Canvas gl={{ alpha: true }} camera={{ fov: 50, near: 0.1, far: 5000 }}>
             {data && <Scene data={data} station={station} ctrlHeld={ctrlHeld} profileAssignments={profileAssignments} cameraQuatRef={cameraQuatRef} centerlineLock={centerlineLock} deviceOrientationRef={deviceOrientationRef} mouseLookRef={mouseLookRef} shouldRecalibrate={shouldRecalibrate} drawMode={drawMode} drawToolType={drawToolType} drawRef={drawRef} brushScale={brushScale} onBrushScaleChange={setBrushScale} enabledScans={enabledScans} onPcLoadingChange={setPcLoadingIndices} salvoData={filteredSalvoData} mwdRawData={effectiveMwdData} mwdRawSensor={effectiveSensor} mwdClampedRange={effectiveClampedRange} categoryColors={effectiveCategoryColors} disabledCategories={disabledCategories.size > 0 ? disabledCategories : null} photos={photos} onPhotosChange={setPhotos} photoCaptureRef={photoCaptureRef} arPhotoActive={arPhotoActive} zoomFovRef={zoomFovRef} onStationChange={setStation} cameraKey={cameraKey} clipPlaneActive={clipPlaneActive} clipPlaneFlipped={clipPlaneFlipped} clipPlaneMatrix={clipPlaneMatrix} onClipPlaneMatrixChange={setClipPlaneMatrix} showMWD={showMWD} mwdOpacity={mwdOpacity} rbfGeometry={rbfGeometry} rbfOpacity={rbfOpacity} rbfSavedLayers={rbfSavedLayers} stereonetFilter={stereonetSelection && stereonetSelection.r > 0.02 ? stereonetSelection : undefined} stereonetFilterIndex={stereonetFilterIndex} />}
        </Canvas>
      </div>

      {/* ── Top bar: title + view toolbar ── */}
      <div style={{
        flexShrink: 0,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        padding: "12px 12px 0",
        zIndex: 2,
        pointerEvents: "none",
        position: "relative",
      }}>
        {/* Title card */}
        <div style={{
          ...sectionCardStyle,
          pointerEvents: "auto",
          padding: "0.55rem 0.65rem",
          display: "inline-flex",
          alignItems: "center",
          gap: "0.55rem",
        }}>
          {onNavigateDashboard && (
            <button
              type="button"
              onClick={onNavigateDashboard}
              title="Back to dashboard"
              style={{
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: 8,
                color: "#f7f8fb",
                cursor: "pointer",
                padding: "4px 8px",
                fontSize: "0.75rem",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: "0.45rem", minWidth: 0 }}>
            <svg width="46" height="22" viewBox="0 0 106 50" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M51.5912 39.1975L51.4896 39.0653C48.5556 34.9206 46.9869 30.0265 46.9434 24.8971C46.8853 18.2981 49.3835 12.0811 53.9442 7.37801C58.5194 2.6749 64.6197 0.0587889 71.1412 0H71.3446C78.113 0 84.4021 2.73369 89.0645 7.71605L89.1807 7.83363L82.848 13.0511L82.7463 12.9483C79.6817 9.90594 75.6293 8.21576 71.3446 8.21576H71.2139C62.2377 8.28924 55.0045 15.7407 55.0771 24.8089C55.1062 28.057 56.0648 31.1728 57.8368 33.8624L57.9094 33.98L57.8077 34.0682L51.5912 39.1828V39.1975Z" fill={NGI_STEEL} />
              <path d="M106 48.7067H97.5469V0.308716H106V48.7067Z" fill={NGI_STEEL} />
              <path d="M82.3395 50V34.0829H65.4185L73.8136 26.9401H89.442V42.916L89.3984 42.9601L82.3541 50H82.3395Z" fill={NGI_RED} />
              <path d="M32.4623 29.9382L0 0.264526V48.4715H8.07564V19.4444L40.5379 48.3833V0.426196H32.4623V29.9382Z" fill={NGI_STEEL} />
            </svg>
            <div style={{ minWidth: 0 }}>
              <h1 style={{ margin: 0, fontSize: "0.92rem", lineHeight: 1.05, fontWeight: 650, letterSpacing: "-0.03em" }}>RockRecord</h1>
              <div style={{ fontSize: "0.62rem", fontWeight: 500, color: "rgba(247,248,251,0.55)", marginTop: 1 }}>3D Survey</div>
            </div>
          </div>
        </div>

        {/* View toolbar */}
        <div style={{
          position: "absolute",
          top: 12,
          right: 12,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 6,
          pointerEvents: "auto",
        }}>
          <AppToolbarButton />
          {hasOrientationSensor ? (
            <ViewToolbarButton
              active={false}
              title={centerlineLock ? "Switch to rotate around point" : "Switch to look around (phone-guided)"}
              onClick={handleToggleLock}
            >
              {centerlineLock ? <IconOrbit /> : <IconLookAround />}
            </ViewToolbarButton>
          ) : (
            <ViewToolbarButton
              active={false}
              title={centerlineLock ? "Switch to rotate around point" : "Switch to look around (mouse-guided — click canvas to capture, Esc to release)"}
              onClick={handleToggleLock}
            >
              {centerlineLock ? <IconOrbit /> : <IconLookAround />}
            </ViewToolbarButton>
          )}
          {centerlineLock && hasOrientationSensor && (
            <ViewToolbarButton
              active={false}
              title="Recalibrate orientation"
              onClick={() => { shouldRecalibrate.current = true; }}
            >
              <IconRecalibrate />
            </ViewToolbarButton>
          )}
          {centerlineLock && hasOrientationSensor && (
            <ViewToolbarButton
              active={gyroEnabled}
              activeColor="#44cc88"
              title={gyroEnabled ? "Disable gyroscope (use finger drag)" : "Enable gyroscope orientation"}
              onClick={() => {
                setGyroEnabled((v) => !v);
                shouldRecalibrate.current = true;
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                {/* phone body */}
                <rect x="7" y="2" width="10" height="20" rx="2" />
                {/* circular arrows around phone */}
                <path d="M5 12a7 7 0 0 1 7-7" />
                <polyline points="12 3 12 5 10 5" />
                <path d="M19 12a7 7 0 0 1-7 7" />
                <polyline points="12 21 12 19 14 19" />
              </svg>
            </ViewToolbarButton>
          )}
          {centerlineLock && hasOrientationSensor && (
            <ViewToolbarButton
              active={arOverlay}
              activeColor="#44cc88"
              title={arOverlay ? "Hide camera preview" : "Show live camera behind 3D (AR)"}
              onClick={() => setArOverlay((v) => !v)}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                {/* video camera icon */}
                <rect x="2" y="5" width="14" height="14" rx="2" />
                <path d="M16 10l6-3v10l-6-3" />
              </svg>
            </ViewToolbarButton>
          )}
        </div>
      </div>

      {/* ── Brush size HUD — persistent when polygon tool active ── */}
      {drawMode && (DRAW_TOOL_CONFIG[drawToolType] as any).isPolygon && (
        <div style={{
          position: "absolute",
          top: 60,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 20,
          pointerEvents: "none",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 4,
        }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "6px 14px",
            borderRadius: 16,
            background: "rgba(0,0,0,0.55)",
            backdropFilter: "blur(12px)",
            border: "1px solid rgba(255,255,255,0.15)",
            color: "#fff",
            fontSize: "0.72rem",
            fontWeight: 600,
          }}>
            <div style={{
              width: Math.max(8, Math.min(40, 20 * brushScale)),
              height: Math.max(8, Math.min(40, 20 * brushScale)),
              borderRadius: "50%",
              border: `2px solid ${DRAW_TOOL_CONFIG[drawToolType].color}`,
              background: DRAW_TOOL_CONFIG[drawToolType].color + "33",
              transition: "all 0.15s ease",
            }} />
            <span>{(DRAW_TOOL_CONFIG[drawToolType].tubeRadius * 12 * brushScale * 2).toFixed(1)}m</span>
          </div>
          <span style={{
            fontSize: "0.55rem",
            color: "rgba(255,255,255,0.45)",
            fontWeight: 500,
          }}>pinch to resize</span>
        </div>
      )}

      {/* ── Middle: sidebar + canvas spacer ── */}
      <div style={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "row",
        gap: 0,
        zIndex: 1,
        pointerEvents: "none",
      }}>
        {/* Sidebar */}
        <div style={{
          flexShrink: 0,
          width: sidebarVisible ? "min(340px, calc(100vw - 88px))" : 0,
          display: "flex",
          flexDirection: "column",
          padding: sidebarVisible ? "0 0 0 12px" : 0,
          pointerEvents: "none",
          zIndex: 2,
          position: "relative",
          transition: "width 0.15s ease",
        }}>
          <button
            type="button"
            onClick={() => setSidebarVisible((v) => !v)}
            aria-label={sidebarVisible ? "Collapse sidebar controls" : "Expand sidebar controls"}
            title={sidebarVisible ? "Collapse sidebar controls" : "Expand sidebar controls"}
            style={{
              position: "absolute",
              top: "50%",
              right: sidebarVisible ? -28 : "auto",
              left: sidebarVisible ? "auto" : 12,
              transform: "translateY(-50%)",
              width: 28,
              height: 84,
              borderRadius: sidebarVisible ? "0 12px 12px 0" : 12,
              border: "1px solid rgba(255,255,255,0.22)",
              borderLeft: sidebarVisible ? "none" : "1px solid rgba(255,255,255,0.22)",
              background: "transparent",
              color: HUD_TEXT,
              cursor: "pointer",
              pointerEvents: "auto",
              display: "grid",
              placeItems: "center",
              padding: 0,
              zIndex: 2,
            }}
          >
            <svg
              aria-hidden="true"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                transform: sidebarVisible ? "rotate(0deg)" : "rotate(180deg)",
                transition: "transform 0.15s ease",
                opacity: 0.9,
                filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.5))",
              }}
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          {sidebarVisible && (
          <div className="hide-scrollbar" style={{
            ...sectionCardStyle,
            pointerEvents: "auto",
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            padding: "0.45rem",
            marginTop: "0.4rem",
          }}>
            <div style={sectionGridStyle}>
              <SidebarItem icon="CL" title="Centerlines" subtitle="Imported layer" active={true} activeColor={NGI_STEEL} onClick={() => {}} compact={true} />
              <SidebarItem icon="XS" title="Cross sections" subtitle="Imported layer" active={true} activeColor={NGI_STEEL} onClick={() => {}} compact={true} />
              <style>{`@keyframes pc-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
              <SidebarItem icon="PC" title="Pointclouds" subtitle={`${enabledScans.size}/${project.pointcloudEndpoints.filter(e => !e.hidden).length} scans`} active={enabledScans.size > 0} activeColor="#44ccaa" onClick={() => setPcExpanded((v) => !v)} compact={true} expanded={pcExpanded} />
              {pcExpanded && (<>
                {project.pointcloudEndpoints.map((ep, idx) => {
                if (ep.hidden) return null;
                const scanMatch = ep.url.match(/scan-(\d+)\.ply/);
                const scanNum = scanMatch ? parseInt(scanMatch[1]) : null;
                const matchingSalvo = scanNum ? salvoData?.salvos.find((s) => s.scan === scanNum) : null;
                const label = matchingSalvo ? `Scan pf ${matchingSalvo.faceStation.toFixed(1)}m` : ep.label;
                const on = enabledScans.has(idx);
                const isLoading = on && pcLoadingIndices.has(idx);
                const cacheStatus = pcCache.statuses[idx] ?? "server_only";
                const dlProgress = pcCache.progress.get(idx);
                const expanded = expandedScans.has(idx);
                return (<React.Fragment key={idx}>
                  <div style={{
                    borderRadius: 8,
                    border: `1px solid ${on ? "#44ccaa33" : "rgba(255,255,255,0.06)"}`,
                    background: on ? "#44ccaa0d" : "rgba(255,255,255,0.02)",
                    padding: "0.3rem 0.5rem",
                    marginLeft: "1.2rem",
                    width: "calc(100% - 1.2rem)",
                  }}>
                    {/* Header row */}
                    <div style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}
                      onClick={() => setExpandedScans((prev) => {
                        const next = new Set(prev);
                        if (next.has(idx)) next.delete(idx); else next.add(idx);
                        return next;
                      })}
                    >
                      <span style={{ fontSize: "0.55rem", opacity: 0.5, flexShrink: 0 }}>{expanded ? "▾" : "▸"}</span>
                      <span style={{
                        flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        fontSize: "0.7rem", fontWeight: 500,
                        color: on ? "#f7f8fb" : "rgba(247,248,251,0.5)",
                      }}>{label}</span>
                      {/* Cache status */}
                      <span title={cacheStatus === "cached" ? "Cached locally" : cacheStatus === "downloading" ? "Downloading..." : "Server only"} style={{
                        fontSize: "0.6rem",
                        color: cacheStatus === "cached" ? "#44ccaa" : cacheStatus === "downloading" ? "#ffaa33" : "rgba(247,248,251,0.3)",
                      }}>
                        {cacheStatus === "cached" ? "💾" : cacheStatus === "downloading" ? "⏳" : "☁️"}
                      </span>
                      {cacheStatus === "server_only" && (
                        <button type="button" title="Download to local cache" onClick={(e) => { e.stopPropagation(); pcCache.downloadScan(idx); }}
                          style={{ background: "none", border: "none", color: "#44ccaa", cursor: "pointer", fontSize: "0.8rem", padding: "2px 4px" }}>⬇</button>
                      )}
                      {cacheStatus === "downloading" && dlProgress !== undefined && (
                        <span style={{ fontSize: "0.6rem", color: "#ffaa33", minWidth: 28 }}>{dlProgress}%</span>
                      )}
                      {cacheStatus === "cached" && (
                        <button type="button" title="Remove from local cache" onClick={(e) => { e.stopPropagation(); pcCache.removeScan(idx); }}
                          style={{ background: "none", border: "none", color: "rgba(247,248,251,0.3)", cursor: "pointer", fontSize: "0.7rem", padding: "2px 4px" }}>✕</button>
                      )}
                      {/* Visibility toggle */}
                      <button
                        type="button"
                        title={on ? "Hide point cloud" : "Show point cloud"}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEnabledScans((prev) => {
                            const next = new Set(prev);
                            if (next.has(idx)) next.delete(idx); else next.add(idx);
                            return next;
                          });
                        }}
                        style={{
                          background: "none", border: "none",
                          color: on ? "#44ccaa" : "rgba(247,248,251,0.4)",
                          cursor: "pointer", fontSize: "0.72rem", padding: "2px 4px", flexShrink: 0,
                        }}
                      >
                        {isLoading ? <span style={{ display: "inline-block", animation: "pc-spin 1s linear infinite", fontSize: "0.65rem" }}>⟳</span> : on ? "●" : "○"}
                      </button>
                    </div>
                    {/* Expanded: Analyses section */}
                    {expanded && (
                      <div style={{ marginTop: "0.3rem", paddingTop: "0.3rem", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                        <div style={{ fontSize: "0.58rem", fontWeight: 600, color: "rgba(247,248,251,0.4)", textTransform: "uppercase", letterSpacing: "0.04em", paddingBottom: "0.15rem" }}>
                          Analyses
                        </div>
                        {(() => {
                          const analysis = ep.analyses?.find(a => a.id.includes("dips"));
                          const hasResults = analysis?.hasResults ?? false;
                          const analysisId = analysis?.id ?? `${idx}-dips`;
                          const analysisOn = enabledAnalyses.has(analysisId);
                          return (<>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "0.2rem 0" }}>
                              <span style={{ fontSize: "0.63rem", color: hasResults ? "rgba(247,248,251,0.7)" : "rgba(247,248,251,0.35)" }}>
                                Dip Analysis
                              </span>
                              {!hasResults && (
                                <button
                                  type="button"
                                  disabled
                                  title="Run analysis (coming soon)"
                                  style={{
                                    background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)",
                                    borderRadius: 4, color: "rgba(247,248,251,0.3)", cursor: "not-allowed",
                                    fontSize: "0.55rem", padding: "1px 6px", fontWeight: 500,
                                  }}
                                >
                                  Run ▶
                                </button>
                              )}
                              {hasResults && (<>
                                <button
                                  type="button"
                                  disabled
                                  title="Re-run analysis (coming soon)"
                                  style={{
                                    background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)",
                                    borderRadius: 4, color: "rgba(247,248,251,0.3)", cursor: "not-allowed",
                                    fontSize: "0.55rem", padding: "1px 6px", fontWeight: 500,
                                  }}
                                >
                                  Rerun
                                </button>
                                <button
                                  type="button"
                                  disabled
                                  title="Clear analysis results (coming soon)"
                                  style={{
                                    background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)",
                                    borderRadius: 4, color: "rgba(247,248,251,0.3)", cursor: "not-allowed",
                                    fontSize: "0.55rem", padding: "1px 6px", fontWeight: 500,
                                  }}
                                >
                                  Clear
                                </button>
                              </>)}
                              <span style={{ flex: 1 }} />
                              {/* Visibility toggle matching the point cloud toggle style */}
                              <button
                                type="button"
                                title={analysisOn ? "Hide analysis" : "Show analysis"}
                                onClick={() => {
                                  if (!hasResults) return;
                                  const willEnable = !enabledAnalyses.has(analysisId);
                                  setEnabledAnalyses((prev) => {
                                    const next = new Set(prev);
                                    if (willEnable) next.add(analysisId); else next.delete(analysisId);
                                    return next;
                                  });
                                  if (analysis?.resultPointcloudIndex != null) {
                                    setEnabledScans((prev) => {
                                      const next = new Set(prev);
                                      if (willEnable) next.add(analysis.resultPointcloudIndex!); else next.delete(analysis.resultPointcloudIndex!);
                                      return next;
                                    });
                                  }
                                }}
                                disabled={!hasResults}
                                style={{
                                  background: "none", border: "none",
                                  color: hasResults ? (analysisOn ? "#44ccaa" : "rgba(247,248,251,0.4)") : "rgba(247,248,251,0.2)",
                                  cursor: hasResults ? "pointer" : "not-allowed",
                                  fontSize: "0.72rem", padding: "2px 4px", flexShrink: 0,
                                }}
                              >
                                {analysisOn ? "●" : "○"}
                              </button>
                            </div>
                            {/* Results image */}
                            {hasResults && analysis?.resultImage && (
                              <div style={{ marginTop: "0.3rem" }}>
                                <div style={{ position: "relative" }}>
                                  {/* Coltop color wheel background */}
                                  {showColtopOverlay && (
                                    <canvas
                                      ref={(canvas) => {
                                        if (!canvas) return;
                                        const size = 512;
                                        if (canvas.width !== size) {
                                          canvas.width = size;
                                          canvas.height = size;
                                          const ctx = canvas.getContext("2d");
                                          if (!ctx) return;
                                          const cx = size / 2, cy = size / 2, r = size / 2;
                                          const imgData = ctx.createImageData(size, size);
                                          for (let y = 0; y < size; y++) {
                                            for (let x = 0; x < size; x++) {
                                              const dx = x - cx, dy = y - cy;
                                              const dist = Math.sqrt(dx * dx + dy * dy);
                                              if (dist > r) continue;
                                              // angle from top (N), clockwise = dip direction
                                              let dipDir = (Math.atan2(dx, -dy) * 180 / Math.PI + 360) % 360;
                                              // dip from 0 (center) to 90 (edge)
                                              const dip = (dist / r) * 90;
                                              // Coltop formula
                                              let h = (540 - dipDir) % 360;
                                              if (h < 0) h += 360;
                                              const rad = dip * Math.PI / 180;
                                              const s = Math.min(1, Math.sqrt(2) * Math.sin(rad / 2));
                                              // HSV to RGB (v=1)
                                              const c2 = s, x2 = c2 * (1 - Math.abs((h / 60) % 2 - 1)), m = 1 - c2;
                                              let r1 = 0, g1 = 0, b1 = 0;
                                              if (h < 60) { r1 = c2; g1 = x2; }
                                              else if (h < 120) { r1 = x2; g1 = c2; }
                                              else if (h < 180) { g1 = c2; b1 = x2; }
                                              else if (h < 240) { g1 = x2; b1 = c2; }
                                              else if (h < 300) { r1 = x2; b1 = c2; }
                                              else { r1 = c2; b1 = x2; }
                                              const i = (y * size + x) * 4;
                                              imgData.data[i] = Math.round((r1 + m) * 255);
                                              imgData.data[i + 1] = Math.round((g1 + m) * 255);
                                              imgData.data[i + 2] = Math.round((b1 + m) * 255);
                                              imgData.data[i + 3] = 200;
                                            }
                                          }
                                          ctx.putImageData(imgData, 0, 0);
                                        }
                                      }}
                                      style={{
                                        position: "absolute", top: "3%", left: "3%", width: "94%", height: "94%",
                                        borderRadius: "50%",
                                        pointerEvents: "none",
                                        zIndex: 0,
                                      }}
                                    />
                                  )}
                                  <img
                                    src={analysis.resultImage}
                                    alt="Dip analysis results"
                                    style={{
                                      width: "100%", borderRadius: 4, border: "1px solid rgba(255,255,255,0.1)",
                                      position: "relative", zIndex: 1,
                                      ...(showColtopOverlay ? { mixBlendMode: "multiply" } : {}),
                                    }}
                                  />
                                  {/* Toggle button */}
                                  <button
                                    type="button"
                                    title={showColtopOverlay ? "Hide color reference" : "Show color reference"}
                                    onClick={() => setShowColtopOverlay(v => !v)}
                                    style={{
                                      position: "absolute", top: 4, right: 4, zIndex: 2,
                                      background: showColtopOverlay ? "rgba(68,204,170,0.25)" : "rgba(255,255,255,0.08)",
                                      border: showColtopOverlay ? "1px solid rgba(68,204,170,0.5)" : "1px solid rgba(255,255,255,0.15)",
                                      borderRadius: 4, padding: "4px 8px",
                                      color: showColtopOverlay ? "#44ccaa" : "rgba(247,248,251,0.5)",
                                      fontSize: "0.6rem", cursor: "pointer", fontWeight: 500,
                                      minWidth: 32, minHeight: 32, display: "flex", alignItems: "center", justifyContent: "center",
                                    }}
                                  >
                                    🎨
                                  </button>
                                  {/* Selection overlay - interaction layer */}
                                  <div
                                    style={{
                                      position: "absolute", top: "3%", left: "3%", width: "94%", height: "94%",
                                      borderRadius: "50%", zIndex: 3, cursor: "crosshair",
                                      touchAction: "none",
                                    }}
                                    onMouseDown={(e) => {
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      const x = (e.clientX - rect.left) / rect.width;
                                      const y = (e.clientY - rect.top) / rect.height;
                                      stereonetDragRef.current = { startX: x, startY: y };
                                      setStereonetSelection({ x, y, r: 0 });
                                    }}
                                    onMouseMove={(e) => {
                                      if (!stereonetDragRef.current) return;
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      const x = (e.clientX - rect.left) / rect.width;
                                      const y = (e.clientY - rect.top) / rect.height;
                                      const dx = x - stereonetDragRef.current.startX;
                                      const dy = y - stereonetDragRef.current.startY;
                                      const r = Math.sqrt(dx * dx + dy * dy);
                                      setStereonetSelection({ x: stereonetDragRef.current.startX, y: stereonetDragRef.current.startY, r });
                                    }}
                                    onMouseUp={() => {
                                      stereonetDragRef.current = null;
                                      if (stereonetSelection && stereonetSelection.r < 0.02) {
                                        setStereonetSelection(null);
                                      }
                                    }}
                                    onMouseLeave={() => {
                                      if (stereonetDragRef.current) {
                                        stereonetDragRef.current = null;
                                      }
                                    }}
                                    onTouchStart={(e) => {
                                      e.preventDefault();
                                      const touch = e.touches[0];
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      const x = (touch.clientX - rect.left) / rect.width;
                                      const y = (touch.clientY - rect.top) / rect.height;
                                      stereonetDragRef.current = { startX: x, startY: y };
                                      setStereonetSelection({ x, y, r: 0 });
                                    }}
                                    onTouchMove={(e) => {
                                      e.preventDefault();
                                      if (!stereonetDragRef.current) return;
                                      const touch = e.touches[0];
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      const x = (touch.clientX - rect.left) / rect.width;
                                      const y = (touch.clientY - rect.top) / rect.height;
                                      const dx = x - stereonetDragRef.current.startX;
                                      const dy = y - stereonetDragRef.current.startY;
                                      const r = Math.sqrt(dx * dx + dy * dy);
                                      setStereonetSelection({ x: stereonetDragRef.current.startX, y: stereonetDragRef.current.startY, r });
                                    }}
                                    onTouchEnd={(e) => {
                                      e.preventDefault();
                                      stereonetDragRef.current = null;
                                      if (stereonetSelection && stereonetSelection.r < 0.02) {
                                        setStereonetSelection(null);
                                      }
                                    }}
                                  />
                                  {/* Selection circle visualization (with angular wrapping) */}
                                  {stereonetSelection && stereonetSelection.r > 0.02 && (() => {
                                    const sel = stereonetSelection;
                                    const cx = sel.x - 0.5;
                                    const cy = sel.y - 0.5;
                                    const selDist = Math.sqrt(cx * cx + cy * cy);
                                    const r = sel.r;
                                    // If selection extends beyond the stereonet edge, show wrapped copy
                                    const needsWrap = selDist + r > 0.5;
                                    // Wrapped position: the center is reflected through the boundary
                                    // i.e. placed at distance (1 - selDist) from center, on the opposite side
                                    const wrapDist = 1.0 - selDist;
                                    const angle = Math.atan2(cx, -cy);
                                    const wrapX = 0.5 - Math.sin(angle) * wrapDist;
                                    const wrapY = 0.5 + Math.cos(angle) * wrapDist;
                                    return (
                                      <svg
                                        viewBox="0 0 1 1"
                                        style={{
                                          position: "absolute", top: "3%", left: "3%", width: "94%", height: "94%",
                                          pointerEvents: "none", zIndex: 4, overflow: "visible",
                                        }}
                                      >
                                        <defs>
                                          <clipPath id="stereonet-clip">
                                            <circle cx="0.5" cy="0.5" r="0.5" />
                                          </clipPath>
                                        </defs>
                                        <g clipPath="url(#stereonet-clip)">
                                          {/* Main selection circle */}
                                          <circle cx={sel.x} cy={sel.y} r={r} fill="rgba(0,0,0,0.25)" stroke="#000000" strokeWidth="0.01" />
                                          {/* Wrapped copy on opposite side */}
                                          {needsWrap && (
                                            <circle cx={wrapX} cy={wrapY} r={r} fill="rgba(0,0,0,0.25)" stroke="#000000" strokeWidth="0.01" strokeDasharray="0.015 0.01" />
                                          )}
                                        </g>
                                      </svg>
                                    );
                                  })()}
                                  {/* Clear selection button */}
                                  {stereonetSelection && stereonetSelection.r > 0.02 && (
                                    <button
                                      type="button"
                                      title="Clear selection"
                                      onClick={() => setStereonetSelection(null)}
                                      style={{
                                        position: "absolute", top: 4, left: 4, zIndex: 5,
                                        background: "rgba(255,68,68,0.2)", border: "1px solid rgba(255,68,68,0.5)",
                                        borderRadius: 4, padding: "4px 8px",
                                        color: "#ff6666", fontSize: "0.6rem", cursor: "pointer", fontWeight: 500,
                                        minWidth: 32, minHeight: 32, display: "flex", alignItems: "center", justifyContent: "center",
                                      }}
                                    >
                                      ✕
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                          </>);
                        })()}
                      </div>
                    )}
                  </div>
                </React.Fragment>);
              })}
              </>)}
              {salvoData && (<>
                <SidebarItem icon="BH" title="Boreholes" subtitle={`${enabledSalvos.size}/${salvoData.salvos.length} rounds`} active={enabledSalvos.size > 0} activeColor="#ff6644" onClick={() => setSalvoExpanded((v) => !v)} compact={true} expanded={salvoExpanded} />
                {salvoExpanded && (<>
                  {[...salvoData.salvos].sort((a, b) => a.faceStation - b.faceStation).map((salvo) => {
                    const on = enabledSalvos.has(salvo.scan);
                    const tableStatuses = salvoTableStatus.get(salvo.scan);
                    const availableTables = project.dataTableSchemas?.filter((schema) => {
                      const status = tableStatuses?.get(schema.id);
                      return status === "available";
                    }) ?? [];
                    return (
                      <button
                        key={salvo.scan}
                        type="button"
                        onClick={() => setEnabledSalvos((prev) => {
                          const next = new Set(prev);
                          if (next.has(salvo.scan)) next.delete(salvo.scan); else next.add(salvo.scan);
                          return next;
                        })}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "0.3rem 0.5rem 0.3rem 2.2rem",
                          borderRadius: 8,
                          border: `1px solid ${on ? "#ff664433" : "rgba(255,255,255,0.06)"}`,
                          background: on ? "#ff66440d" : "rgba(255,255,255,0.02)",
                          color: on ? "#f7f8fb" : "rgba(247,248,251,0.5)",
                          cursor: "pointer",
                          fontSize: "0.7rem",
                          fontWeight: 500,
                          textAlign: "left",
                          width: "100%",
                        }}
                      >
                        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{salvo.scan >= 100 ? `Injection pf ${salvo.faceStation.toFixed(1)}m (${salvo.holes.length} holes)` : `Salvo pf ${salvo.faceStation.toFixed(1)}m (${salvo.holes.length} holes)`}</span>
                        {/* Per-salvo data table availability badges */}
                        {on && availableTables.length > 0 && (
                          <span style={{ fontSize: "0.5rem", color: "#44aaff", opacity: 0.7 }} title={`${availableTables.length} table${availableTables.length > 1 ? "s" : ""}`}>
                            &#9638;{availableTables.length}
                          </span>
                        )}
                        {on && tableStatuses?.get("mwd_raw") === "loading" && (
                          <span style={{ fontSize: "0.5rem", color: "rgba(247,248,251,0.4)" }}>...</span>
                        )}
                        <span style={{
                          fontSize: "0.72rem",
                          color: on ? "#ff6644" : "rgba(247,248,251,0.4)",
                        }}>{on ? "●" : "○"}</span>
                      </button>
                    );
                  })}
                </>)}
              </>)}
              {/* ── Borehole Data (top-level) ── */}
              {project.salvoEndpoint && (<>
                <SidebarItem icon="DT" title="Borehole Data" subtitle={
                  !project.dataTableSchemas?.length ? "No data tables" :
                  enabledSalvos.size === 0 ? "Enable boreholes first" :
                  (showMwdRaw && mwdRawSensor) ? `MWD: ${mwdRawSensor}` :
                  showMWD ? "MWD Interpretations" :
                  (activeExtraSchema && activeColumnName) ? `${activeExtraSchema.name}: ${activeColumnName}` :
                  `${project.dataTableSchemas.length} table${project.dataTableSchemas.length !== 1 ? "s" : ""}`
                } active={!!project.dataTableSchemas?.length} activeColor="#44aaff" onClick={() => { if (project.dataTableSchemas?.length && enabledSalvos.size > 0) setDataTablesExpanded((v) => !v); }} compact={true} expanded={project.dataTableSchemas?.length && enabledSalvos.size > 0 ? dataTablesExpanded : undefined} />
                {dataTablesExpanded && project.dataTableSchemas && (<>
                  {project.dataTableSchemas.map((schema) => {
                    const isActive = activeTableId === schema.id;
                    const salvoCount = [...enabledSalvos].filter((s) => salvoTableStatus.get(s)?.get(schema.id) === "available").length;
                    const isRawMwd = schema.id === "mwd_raw";
                    const isInterp = schema.id === "mwd_interp";
                    const noSalvos = enabledSalvos.size === 0;
                    return (
                      <div key={schema.id}>
                        <button
                          type="button"
                          disabled={noSalvos}
                          onClick={() => {
                            if (noSalvos) return;
                            if (isRawMwd) {
                              setShowMwdRaw((v) => !v);
                              if (!showMwdRaw) { setActiveTableId(schema.id); setActiveColumnName(null); }
                              else { setActiveTableId(null); setActiveColumnName(null); }
                            } else if (isInterp) {
                              if (project.mwdEndpoint) setShowMWD((v) => !v);
                              if (!showMWD) { setActiveTableId(schema.id); }
                              else { setActiveTableId(null); setActiveColumnName(null); }
                            } else {
                              if (!isActive) { setShowMwdRaw(false); }
                              setActiveTableId(isActive ? null : schema.id);
                              setActiveColumnName(null);
                            }
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "0.3rem 0.5rem 0.3rem 2.2rem",
                            borderRadius: 8,
                            border: `1px solid ${(isRawMwd ? showMwdRaw : isInterp ? showMWD : isActive) ? "#44aaff33" : "rgba(255,255,255,0.06)"}`,
                            background: (isRawMwd ? showMwdRaw : isInterp ? showMWD : isActive) ? "rgba(68,170,255,0.06)" : "rgba(255,255,255,0.02)",
                            color: (isRawMwd ? showMwdRaw : isInterp ? showMWD : isActive) ? "#f7f8fb" : "rgba(247,248,251,0.5)",
                            cursor: noSalvos ? "not-allowed" : "pointer",
                            opacity: noSalvos ? 0.4 : 1,
                            fontSize: "0.68rem",
                            fontWeight: 500,
                            textAlign: "left",
                            width: "100%",
                          }}
                        >
                          <span style={{ flex: 1 }}>{schema.name}</span>
                          <span style={{ fontSize: "0.5rem", color: "rgba(247,248,251,0.4)" }}>{salvoCount}/{enabledSalvos.size}</span>
                          <span style={{ fontSize: "0.55rem", color: (isRawMwd ? (showMwdRaw && mwdRawSensor) : isInterp ? showMWD : (isActive && activeColumnName)) ? "#44aaff" : "rgba(247,248,251,0.4)" }}>
                            {(isRawMwd ? (showMwdRaw && mwdRawSensor) : isInterp ? showMWD : (isActive && activeColumnName)) ? "●" : "○"}
                          </span>
                        </button>
                        {/* Column picker for MWD Raw */}
                        {isRawMwd && showMwdRaw && (
                      <div style={{ padding: "0.2rem 0.5rem 0.2rem 2.8rem", display: "flex", flexDirection: "column", gap: 4 }}>
                        <select
                          value={mwdRawSensor ?? ""}
                          onChange={(e) => { setMwdRawSensor((e.target.value || null) as MwdSensorName | null); setMwdRawClamp([0, 100]); }}
                          style={{
                            background: "rgba(255,255,255,0.08)",
                            color: "#f7f8fb",
                            border: "1px solid rgba(255,255,255,0.15)",
                            borderRadius: 6,
                            padding: "0.3rem 0.4rem",
                            fontSize: "0.68rem",
                            cursor: "pointer",
                            width: "100%",
                          }}
                        >
                          <option value="" style={{ background: "#1a1a2e" }}>— Select column —</option>
                          {MWD_SENSORS.map((s) => (
                            <option key={s} value={s} style={{ background: "#1a1a2e" }}>{s}</option>
                          ))}
                        </select>
                        {mwdRawSensor && mwdSensorRange && (() => {
                          const mwdSchema = project.dataTableSchemas?.find((s) => s.id === "mwd_raw");
                          const colDef = mwdSchema?.columns.find((c) => c.name === mwdRawSensor);
                          const ramp = colDef?.colorRamp ?? "green-red";
                          const isReversed = ramp === "red-green" || ramp === "red-blue";
                          const fullRange = mwdSensorRange.max - mwdSensorRange.min;
                          const clampMin = mwdSensorRange.min + (mwdRawClamp[0] / 100) * fullRange;
                          const clampMax = mwdSensorRange.min + (mwdRawClamp[1] / 100) * fullRange;
                          const leftVal = isReversed ? clampMax : clampMin;
                          const rightVal = isReversed ? clampMin : clampMax;
                          const midVal = (clampMin + clampMax) / 2;
                          const gradientColors = ramp === "green-red" ? "#22cc44, #cccc00, #dd3322"
                            : ramp === "red-green" ? "#dd3322, #cccc00, #22cc44"
                            : ramp === "blue-red" ? "#2244dd, #cc44cc, #dd3322"
                            : "#dd3322, #cc44cc, #2244dd";
                          return (
                            <div style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 3,
                              fontSize: "0.6rem",
                              color: "rgba(247,248,251,0.7)",
                            }}>
                              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.55rem", opacity: 0.6 }}>
                                <span>{isReversed ? "Poor rock" : "Good rock"}</span>
                                <span>{isReversed ? "Good rock" : "Poor rock"}</span>
                              </div>
                              <div style={{ position: "relative", width: "100%", height: 12, borderRadius: 4, overflow: "hidden" }}>
                                <div style={{ position: "absolute", inset: 0, background: `linear-gradient(90deg, ${gradientColors})` }} />
                                {mwdRawClamp[0] > 0 && (
                                  <div style={{
                                    position: "absolute", top: 0, bottom: 0, left: 0,
                                    width: `${mwdRawClamp[0]}%`,
                                    background: "rgba(30,30,40,0.8)",
                                  }} />
                                )}
                                {mwdRawClamp[1] < 100 && (
                                  <div style={{
                                    position: "absolute", top: 0, bottom: 0, right: 0,
                                    width: `${100 - mwdRawClamp[1]}%`,
                                    background: "rgba(30,30,40,0.8)",
                                  }} />
                                )}
                              </div>
                              <div style={{ position: "relative", width: "100%", height: 16 }}>
                                <input
                                  type="range" min={0} max={100} value={mwdRawClamp[0]}
                                  onChange={(e) => {
                                    const v = Math.min(Number(e.target.value), mwdRawClamp[1] - 2);
                                    setMwdRawClamp([v, mwdRawClamp[1]]);
                                  }}
                                  style={{
                                    position: "absolute", top: 0, left: 0, width: "100%",
                                    appearance: "none", WebkitAppearance: "none",
                                    background: "transparent", pointerEvents: "none",
                                    height: 16, margin: 0, zIndex: 2,
                                  }}
                                  className="mwd-range-thumb"
                                />
                                <input
                                  type="range" min={0} max={100} value={mwdRawClamp[1]}
                                  onChange={(e) => {
                                    const v = Math.max(Number(e.target.value), mwdRawClamp[0] + 2);
                                    setMwdRawClamp([mwdRawClamp[0], v]);
                                  }}
                                  style={{
                                    position: "absolute", top: 0, left: 0, width: "100%",
                                    appearance: "none", WebkitAppearance: "none",
                                    background: "transparent", pointerEvents: "none",
                                    height: 16, margin: 0, zIndex: 2,
                                  }}
                                  className="mwd-range-thumb"
                                />
                              </div>
                              <style>{`
                                .mwd-range-thumb::-webkit-slider-thumb {
                                  -webkit-appearance: none;
                                  appearance: none;
                                  width: 12px; height: 12px;
                                  border-radius: 50%;
                                  background: #f7f8fb;
                                  border: 2px solid #44aaff;
                                  cursor: pointer;
                                  pointer-events: auto;
                                  box-shadow: 0 1px 3px rgba(0,0,0,0.4);
                                }
                                .mwd-range-thumb::-moz-range-thumb {
                                  width: 12px; height: 12px;
                                  border-radius: 50%;
                                  background: #f7f8fb;
                                  border: 2px solid #44aaff;
                                  cursor: pointer;
                                  pointer-events: auto;
                                  box-shadow: 0 1px 3px rgba(0,0,0,0.4);
                                }
                              `}</style>
                              <div style={{ display: "flex", justifyContent: "space-between", fontVariantNumeric: "tabular-nums" }}>
                                <span>{leftVal.toFixed(1)}</span>
                                <span>{midVal.toFixed(1)}</span>
                                <span>{rightVal.toFixed(1)}</span>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                    {/* Column picker for MWD Interpretations (categorical) */}
                    {isInterp && showMWD && mwdLegend.length > 0 && (
                      <div style={{ padding: "0.1rem 0.5rem 0.1rem 2.8rem", display: "flex", flexDirection: "column", gap: 2 }}>
                        {mwdLegend.map((entry) => (
                          <div
                            key={entry.rockType}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "0.2rem 0",
                              fontSize: "0.64rem",
                              color: "rgba(247,248,251,0.8)",
                            }}
                          >
                            <span style={{
                              width: 10,
                              height: 10,
                              borderRadius: 2,
                              background: entry.color,
                              flexShrink: 0,
                            }} />
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{entry.rockType.replace(/_/g, " ")}</span>
                          </div>
                        ))}
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "0.2rem 0",
                          fontSize: "0.62rem",
                          color: "rgba(247,248,251,0.6)",
                        }}>
                          <span style={{ flexShrink: 0 }}>Opacity</span>
                          <input
                            type="range"
                            min={0}
                            max={100}
                            value={Math.round(mwdOpacity * 100)}
                            onChange={(e) => setMwdOpacity(Number(e.target.value) / 100)}
                            style={{ flex: 1, accentColor: "#cc88ff", height: 4, cursor: "pointer" }}
                          />
                          <span style={{ width: 28, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{Math.round(mwdOpacity * 100)}%</span>
                        </div>
                      </div>
                    )}
                    {/* Column picker for generic data tables */}
                    {!isRawMwd && !isInterp && isActive && (
                      <div style={{ padding: "0.2rem 0.5rem 0.2rem 2.8rem", display: "flex", flexDirection: "column", gap: 4 }}>
                        <select
                          value={activeColumnName ?? ""}
                          onChange={(e) => { setActiveColumnName(e.target.value || null); setDisabledCategories(new Set()); }}
                          style={{
                            background: "rgba(255,255,255,0.08)",
                            color: "#f7f8fb",
                            border: "1px solid rgba(255,255,255,0.15)",
                            borderRadius: 6,
                            padding: "0.3rem 0.4rem",
                            fontSize: "0.68rem",
                            cursor: "pointer",
                            width: "100%",
                          }}
                        >
                          <option value="" style={{ background: "#1a1a2e" }}>— Select column —</option>
                          {schema.columns.map((col) => (
                            <option key={col.name} value={col.name} style={{ background: "#1a1a2e" }}>{col.name}</option>
                          ))}
                        </select>
                        {/* Category legend for categorical columns */}
                        {activeColumnName && (() => {
                          const col = schema.columns.find((c) => c.name === activeColumnName);
                          if (!col || col.type !== "categorical" || !col.categories) return null;
                          return (
                            <div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 2 }}>
                              {col.categories.map((cat) => {
                                const isDisabled = disabledCategories.has(cat.value);
                                return (
                                <div
                                  key={cat.value}
                                  onClick={() => {
                                    setDisabledCategories((prev) => {
                                      const next = new Set(prev);
                                      if (next.has(cat.value)) next.delete(cat.value);
                                      else next.add(cat.value);
                                      return next;
                                    });
                                  }}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    padding: "0.2rem 0",
                                    fontSize: "0.64rem",
                                    color: isDisabled ? "rgba(247,248,251,0.35)" : "rgba(247,248,251,0.8)",
                                    cursor: "pointer",
                                    userSelect: "none",
                                  }}
                                >
                                  <span style={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: 2,
                                    background: isDisabled ? "rgba(255,255,255,0.1)" : cat.color,
                                    border: isDisabled ? "1px solid rgba(255,255,255,0.2)" : "none",
                                    flexShrink: 0,
                                  }} />
                                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: isDisabled ? "line-through" : "none" }}>{cat.label ?? cat.value.replace(/_/g, " ")}</span>
                                </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                        </div>
                      );
                    })}
                  </>)}
              </>)}
              {/* ── Geological Modeling ── */}
              <SidebarItem icon="GM" title="Geological Modeling" subtitle={`${rbfSavedLayers.length} layer${rbfSavedLayers.length !== 1 ? "s" : ""}`} active={rbfSavedLayers.some((l) => l.visible)} activeColor="#ff4422" onClick={() => setGeoModelExpanded((v) => !v)} compact={true} expanded={geoModelExpanded} />
              {geoModelExpanded && (<>
                {/* + New button */}
                <button
                  type="button"
                  onClick={() => { setGeoModelCreating((v) => { if (!v) setSidebarVisible(false); return !v; }); }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "0.35rem 0.5rem 0.35rem 2.2rem",
                    borderRadius: 8,
                    border: `1px solid ${geoModelCreating ? "rgba(255,34,0,0.4)" : "rgba(255,255,255,0.12)"}`,
                    background: geoModelCreating ? "rgba(255,34,0,0.1)" : "rgba(255,255,255,0.04)",
                    color: geoModelCreating ? "#f7f8fb" : "rgba(247,248,251,0.7)",
                    cursor: "pointer",
                    fontSize: "0.68rem",
                    fontWeight: 600,
                    textAlign: "left",
                    width: "100%",
                  }}
                >
                  <span style={{ fontSize: "0.8rem", lineHeight: 1 }}>+</span>
                  <span style={{ flex: 1 }}>New</span>
                  {geoModelCreating && <span style={{ fontSize: "0.55rem", opacity: 0.5 }}>▾</span>}
                </button>
                {/* Saved layers list */}
                {rbfSavedLayers.map((layer) => (
                  <div key={layer.id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "0.3rem 0.5rem 0.3rem 2.2rem", fontSize: "0.66rem", color: "rgba(247,248,251,0.75)" }}>
                    <button
                      type="button"
                      onClick={() => {
                        setRbfSavedLayers((prev) => prev.map((l) => l.id === layer.id ? { ...l, visible: !l.visible } : l));
                        updateRBFLayerVisibility(project.id, layer.id, !layer.visible).catch(() => {});
                      }}
                      style={{
                        width: 16, height: 16, borderRadius: 4, flexShrink: 0, cursor: "pointer",
                        background: layer.visible ? "rgba(255,34,0,0.4)" : "rgba(255,255,255,0.08)",
                        border: `1px solid ${layer.visible ? "rgba(255,34,0,0.7)" : "rgba(255,255,255,0.2)"}`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "0.5rem", color: "#fff",
                      }}
                    >
                      {layer.visible ? "✓" : ""}
                    </button>
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{layer.name}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setRbfSavedLayers((prev) => prev.filter((l) => l.id !== layer.id));
                        deleteRBFLayer(project.id, layer.id).catch(() => {});
                      }}
                      style={{
                        background: "none", border: "none", color: "rgba(247,248,251,0.4)",
                        cursor: "pointer", fontSize: "0.7rem", padding: "0 4px",
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </>)}
              <SidebarItem icon="&#9986;" title="Section plane" subtitle={clipPlaneActive ? "Drag gizmo to slice" : "Clip the model"} active={clipPlaneActive} activeColor="#aa66ff" onClick={() => setClipPlaneActive((v) => !v)} compact={true} />
              {clipPlaneActive && (
                <button
                  type="button"
                  onClick={() => setClipPlaneFlipped((v) => !v)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "0.3rem 0.5rem 0.3rem 2.2rem",
                    borderRadius: 8,
                    border: "1px solid rgba(170,102,255,0.25)",
                    background: "rgba(170,102,255,0.08)",
                    color: "#f7f8fb",
                    cursor: "pointer",
                    fontSize: "0.7rem",
                    fontWeight: 500,
                    textAlign: "left",
                    width: "100%",
                  }}
                >
                  <span style={{ flex: 1 }}>Flip direction</span>
                  <span style={{
                    fontSize: "0.58rem",
                    fontWeight: 800,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase" as const,
                    padding: "0.15rem 0.35rem",
                    borderRadius: 999,
                    background: "rgba(170,102,255,0.15)",
                    border: "1px solid rgba(170,102,255,0.3)",
                  }}>{clipPlaneFlipped ? "B" : "A"}</span>
                </button>
              )}
            </div>
          </div>
          )}
        </div>

        {/* Canvas area — empty spacer; canvas renders behind via fixed background layer */}
        <div style={{ flex: 1, minWidth: 0 }} />
      </div>

      {/* Hidden file input for photo capture */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handlePhotoCapture}
        style={{ display: "none" }}
      />

      {/* ── Bottom bar: action buttons + station slider ── */}
      <div className="bottom-bar" style={{
        flexShrink: 0,
        zIndex: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 8,
        padding: "10px 1.5rem 12px",
        background: "linear-gradient(transparent, rgba(3, 10, 18, 0.72))",
      }}>
        {/* Action bar — replaced by geo model creation panel when active */}
        {geoModelCreating ? (
          <div style={{
            width: "100%",
            maxWidth: 480,
            display: "flex",
            flexDirection: "column",
            gap: 8,
            padding: "10px 14px",
            borderRadius: 16,
            background: GLASS_PANEL,
            border: "1px solid rgba(255,68,34,0.3)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
            pointerEvents: "auto",
          }}>
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "#f7f8fb" }}>New Geological Model</span>
              <button
                type="button"
                onClick={() => { setGeoModelCreating(false); setRbfGeometry(null); setRbfGrid(null); setRbfStatus(null); }}
                style={{
                  background: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  borderRadius: 6,
                  color: "rgba(247,248,251,0.7)",
                  padding: "0.25rem 0.6rem",
                  fontSize: "0.65rem",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
            {/* Source info */}
            {(!showMwdRaw || !mwdRawSensor) && (!activeExtraColumn || !activeColumnName) ? (
              <span style={{ fontSize: "0.7rem", color: "rgba(247,248,251,0.5)", fontStyle: "italic" }}>
                Select a data table column under Boreholes first, then come back here
              </span>
            ) : !rbfGrid ? (<>
              {/* ── Step 1: Configure + Generate ── */}
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.68rem", color: "rgba(247,248,251,0.5)" }}>
                <span>Source: <span style={{ color: "#44aaff" }}>{activeExtraColumn ? `${activeExtraSchema!.name} → ${activeColumnName}` : `MWD Raw → ${mwdRawSensor}`}</span></span>
                <span>{[...enabledSalvos].filter((s) => salvoTableStatus.get(s)?.get(activeExtraSchema?.id ?? "mwd_raw") === "available").length} salvos</span>
              </div>
              {/* Category selection for categorical columns */}
              {activeExtraColumn?.type === "categorical" && activeExtraColumn.categories && (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span style={{ fontSize: "0.68rem", color: "rgba(247,248,251,0.6)", fontWeight: 500 }}>Select categories for surface (inside):</span>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {activeExtraColumn.categories.map((cat) => (
                      <button
                        key={cat.value}
                        type="button"
                        onClick={() => {
                          setRbfSelectedCategories((prev) => {
                            const next = new Set(prev);
                            if (next.has(cat.value)) next.delete(cat.value);
                            else next.add(cat.value);
                            return next;
                          });
                        }}
                        style={{
                          background: rbfSelectedCategories.has(cat.value) ? cat.color + "44" : "rgba(255,255,255,0.04)",
                          border: `1px solid ${rbfSelectedCategories.has(cat.value) ? cat.color : "rgba(255,255,255,0.1)"}`,
                          borderRadius: 6,
                          color: rbfSelectedCategories.has(cat.value) ? "#f7f8fb" : "rgba(247,248,251,0.5)",
                          padding: "0.25rem 0.5rem",
                          fontSize: "0.65rem",
                          cursor: "pointer",
                        }}
                      >
                        <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: cat.color, marginRight: 4 }} />
                        {cat.label || cat.value}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.75rem", color: "rgba(247,248,251,0.7)" }}>
                <span style={{ width: 72, flexShrink: 0, fontWeight: 500 }}>Radius</span>
                <input type="range" min={1} max={20} step={0.5} value={rbfRadius}
                  onChange={(e) => setRbfRadius(Number(e.target.value))}
                  style={{ flex: 1, accentColor: "#ff4422", height: 6, cursor: "pointer" }} />
                <span style={{ width: 32, textAlign: "right", fontVariantNumeric: "tabular-nums", fontSize: "0.72rem" }}>{rbfRadius}m</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.75rem", color: "rgba(247,248,251,0.7)" }}>
                <span style={{ width: 72, flexShrink: 0, fontWeight: 500 }}>Flatness</span>
                <input type="range" min={1} max={20} step={0.5} value={rbfFlatness}
                  onChange={(e) => setRbfFlatness(Number(e.target.value))}
                  style={{ flex: 1, accentColor: "#ff4422", height: 6, cursor: "pointer" }} />
                <span style={{ width: 32, textAlign: "right", fontVariantNumeric: "tabular-nums", fontSize: "0.72rem" }}>{rbfFlatness}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.75rem", color: "rgba(247,248,251,0.7)" }}>
                <span style={{ width: 72, flexShrink: 0, fontWeight: 500 }}>Resolution</span>
                <input type="range" min={10} max={50} value={rbfResolution}
                  onChange={(e) => setRbfResolution(Number(e.target.value))}
                  style={{ flex: 1, accentColor: "#ff4422", height: 6, cursor: "pointer" }} />
                <span style={{ width: 32, textAlign: "right", fontVariantNumeric: "tabular-nums", fontSize: "0.72rem" }}>{rbfResolution}</span>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={() => { setRbfDrawingPlane(true); setRbfDrawLine(null); }}
                  style={{
                    flex: 1,
                    background: rbfDrawnNormal ? "rgba(0,200,100,0.2)" : "rgba(255,34,0,0.15)",
                    border: `1px solid ${rbfDrawnNormal ? "rgba(0,200,100,0.5)" : "rgba(255,34,0,0.5)"}`,
                    borderRadius: 8,
                    color: "#f7f8fb",
                    padding: "0.45rem 0.8rem",
                    fontSize: "0.75rem",
                    cursor: "pointer",
                    fontWeight: 500,
                  }}
                >
                  {rbfDrawnNormal ? "Redraw Plane" : "Draw Plane"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const isCategorical = activeExtraColumn?.type === "categorical";
                    const effectiveData = isCategorical ? extraTableData.get(activeExtraSchema!.id) : mwdRawData;
                    const effectiveSensorName = isCategorical ? (activeColumnName as MwdSensorName) : mwdRawSensor;
                    if (!data || !filteredSalvoData || !effectiveData?.size || !effectiveSensorName) return;
                    if (isCategorical && rbfSelectedCategories.size === 0) { setRbfStatus("Select at least one category"); return; }
                    if (!isCategorical && !mwdClampedRange) return;
                    setRbfStatus("Computing...");
                    setTimeout(() => {
                      if (isCategorical) {
                        // Generate one volume per selected category with its own color
                        const categories = [...rbfSelectedCategories];
                        const catColors = new Map<string, string>();
                        for (const cat of activeExtraColumn!.categories!) {
                          catColors.set(cat.value, cat.color);
                        }
                        const pendingResults: { name: string; grid: ScalarGrid; geometry: THREE.BufferGeometry; color: string }[] = [];
                        let totalTri = 0;
                        for (const catValue of categories) {
                          const singleSet = new Set([catValue]);
                          const result = generateRBFVolume({
                            data,
                            salvoData: filteredSalvoData,
                            mwdRawData: effectiveData,
                            sensor: effectiveSensorName,
                            selectedCategories: singleSet,
                            resolution: rbfResolution,
                            radius: rbfRadius,
                            flatness: rbfFlatness,
                            isoValue: rbfIsoValue,
                            planeNormal: rbfDrawnNormal ?? undefined,
                            onProgress: (s) => setRbfStatus(`${catValue}: ${s}`),
                          });
                          if (result) {
                            const label = activeExtraColumn!.categories!.find(c => c.value === catValue)?.label || catValue;
                            pendingResults.push({ name: label, grid: result.grid, geometry: result.geometry, color: catColors.get(catValue) || "#ff2200" });
                            totalTri += result.triangleCount;
                          }
                        }
                        if (pendingResults.length > 0) {
                          setRbfPendingCatLayers(pendingResults);
                          // Set rbfGrid to first result so Step 2 UI appears
                          setRbfGrid(pendingResults[0].grid);
                          setRbfGeometry(pendingResults[0].geometry);
                          setRbfStatus(`${pendingResults.length} volumes, ${totalTri} tri`);
                        } else {
                          setRbfStatus("No surfaces generated");
                        }
                      } else {
                        const result = generateRBFVolume({
                          data,
                          salvoData: filteredSalvoData,
                          mwdRawData: effectiveData,
                          sensor: effectiveSensorName,
                          clampRange: mwdClampedRange!,
                          resolution: rbfResolution,
                          radius: rbfRadius,
                          flatness: rbfFlatness,
                          isoValue: rbfIsoValue,
                          planeNormal: rbfDrawnNormal ?? undefined,
                          onProgress: setRbfStatus,
                        });
                        if (result) {
                          setRbfGeometry(result.geometry);
                          setRbfGrid(result.grid);
                          setRbfStatus(`${result.triangleCount} tri, ${result.timeMs.toFixed(0)}ms`);
                        } else {
                          setRbfStatus("No surface generated");
                          setRbfGrid(null);
                        }
                      }
                    }, 50);
                  }}
                  style={{
                    flex: 1,
                    background: "rgba(255,34,0,0.15)",
                    border: "1px solid rgba(255,34,0,0.5)",
                    borderRadius: 8,
                    color: "#f7f8fb",
                    padding: "0.45rem 0.8rem",
                    fontSize: "0.78rem",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  Generate
                </button>
              </div>
              {rbfStatus && (
                <span style={{ fontSize: "0.68rem", color: "rgba(247,248,251,0.5)" }}>{rbfStatus}</span>
              )}
            </>) : (<>
              {/* ── Step 2: Refine + Save ── */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.75rem", color: "rgba(247,248,251,0.7)" }}>
                <span style={{ width: 72, flexShrink: 0, fontWeight: 500 }}>Cut-off</span>
                <input type="range" min={5} max={95} value={Math.round(rbfIsoValue * 100)}
                  onChange={(e) => {
                    const iso = Number(e.target.value) / 100;
                    setRbfIsoValue(iso);
                    if (rbfPendingCatLayers) {
                      // Re-extract all categorical grids
                      let totalTri = 0;
                      const updated = rbfPendingCatLayers.map((layer) => {
                        const geo = extractSurfaceFromGrid(layer.grid, iso);
                        if (geo) totalTri += geo.getAttribute("position").count / 3;
                        return { ...layer, geometry: geo || layer.geometry };
                      });
                      setRbfPendingCatLayers(updated);
                      setRbfGeometry(updated[0]?.geometry || null);
                      setRbfStatus(`${updated.length} volumes, ${totalTri} tri`);
                    } else {
                      const geo = extractSurfaceFromGrid(rbfGrid, iso);
                      setRbfGeometry(geo);
                      setRbfStatus(geo ? `${(geo.getAttribute("position").count / 3)} tri` : "No surface");
                    }
                  }}
                  style={{ flex: 1, accentColor: "#ff8844", height: 6, cursor: "pointer" }} />
                <span style={{ width: 32, textAlign: "right", fontVariantNumeric: "tabular-nums", fontSize: "0.72rem" }}>{(rbfIsoValue * 100).toFixed(0)}%</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.75rem", color: "rgba(247,248,251,0.7)" }}>
                <span style={{ width: 72, flexShrink: 0, fontWeight: 500 }}>Opacity</span>
                <input type="range" min={5} max={100} value={rbfOpacity}
                  onChange={(e) => setRbfOpacity(Number(e.target.value))}
                  style={{ flex: 1, accentColor: "#ff8844", height: 6, cursor: "pointer" }} />
                <span style={{ width: 32, textAlign: "right", fontVariantNumeric: "tabular-nums", fontSize: "0.72rem" }}>{rbfOpacity}%</span>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  type="text"
                  placeholder="Layer name..."
                  value={rbfSaveName}
                  onChange={(e) => setRbfSaveName(e.target.value)}
                  style={{
                    flex: 1,
                    background: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.2)",
                    borderRadius: 6,
                    color: "#f7f8fb",
                    padding: "0.4rem 0.6rem",
                    fontSize: "0.75rem",
                    outline: "none",
                    minWidth: 0,
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    if (rbfPendingCatLayers) {
                      // Save all pending categorical layers
                      const newLayers: RBFLayer[] = [];
                      for (const pending of rbfPendingCatLayers) {
                        const id = ++rbfLayerIdRef.current;
                        const layer: RBFLayer = { id, name: pending.name, geometry: pending.geometry, visible: true, opacity: rbfOpacity / 100, color: pending.color };
                        newLayers.push(layer);
                        saveRBFLayer(project.id, layer).catch((err) => console.warn("[RBF Store] Save failed:", err));
                      }
                      setRbfSavedLayers((prev) => [...prev, ...newLayers]);
                      setRbfPendingCatLayers(null);
                    } else {
                      if (!rbfGeometry) return;
                      const id = ++rbfLayerIdRef.current;
                      const name = rbfSaveName.trim() || `Layer ${id}`;
                      const layer: RBFLayer = { id, name, geometry: rbfGeometry, visible: true, opacity: rbfOpacity / 100, color: "#ff2200" };
                      setRbfSavedLayers((prev) => [...prev, layer]);
                      saveRBFLayer(project.id, layer).catch((err) => console.warn("[RBF Store] Save failed:", err));
                    }
                    setRbfSaveName("");
                    setRbfGeometry(null);
                    setRbfGrid(null);
                    setRbfStatus(null);
                    setGeoModelCreating(false);
                  }}
                  style={{
                    background: "rgba(0,200,100,0.25)",
                    border: "1px solid rgba(0,200,100,0.5)",
                    borderRadius: 6,
                    color: "#f7f8fb",
                    padding: "0.4rem 0.8rem",
                    fontSize: "0.75rem",
                    cursor: "pointer",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  Save
                </button>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={() => { setRbfGeometry(null); setRbfGrid(null); setRbfStatus(null); setRbfPendingCatLayers(null); }}
                  style={{
                    flex: 1,
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.15)",
                    borderRadius: 6,
                    color: "rgba(247,248,251,0.7)",
                    padding: "0.4rem 0.6rem",
                    fontSize: "0.72rem",
                    cursor: "pointer",
                  }}
                >
                  Back to settings
                </button>
              </div>
              {rbfStatus && (
                <span style={{ fontSize: "0.68rem", color: "rgba(247,248,251,0.5)" }}>{rbfStatus}</span>
              )}
            </>)}
          </div>
        ) : (
        <div style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 10px",
          borderRadius: 18,
          background: GLASS_PANEL,
          border: "1px solid rgba(255,255,255,0.16)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
        }}>
          {/* Sub-tool picker — centered over action bar */}
          {drawPickerOpen && (
            <div
              style={{
                position: "absolute",
                bottom: "calc(100% + 10px)",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 16,
                background: GLASS_PANEL,
                border: "1px solid rgba(255,255,255,0.2)",
                backdropFilter: "blur(16px)",
                WebkitBackdropFilter: "blur(16px)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
                whiteSpace: "nowrap",
                zIndex: 10,
              }}
            >
              {(Object.keys(DRAW_TOOL_CONFIG) as DrawToolType[]).map((tool) => {
                const cfg = DRAW_TOOL_CONFIG[tool];
                const isHovered = drawPickerHover === tool;
                const isActive = drawToolType === tool && drawMode;
                const highlighted = isHovered || isActive;
                return (
                  <button
                    key={tool}
                    ref={(el) => { if (el) drawToolRefs.current.set(tool, el); }}
                    type="button"
                    tabIndex={-1}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 4,
                      padding: "8px 14px",
                      borderRadius: 12,
                      cursor: "pointer",
                      pointerEvents: "auto",
                      color: highlighted ? "#fff" : "rgba(247,248,251,0.85)",
                      background: highlighted ? cfg.activeColor + "44" : "rgba(255,255,255,0.06)",
                      border: `1.5px solid ${highlighted ? cfg.activeColor + "cc" : "rgba(255,255,255,0.15)"}`,
                      boxShadow: highlighted ? `0 0 16px ${cfg.activeColor}66` : "none",
                      transform: isHovered ? "scale(1.08)" : "scale(1)",
                      transition: "all 0.1s ease",
                      minWidth: 64,
                    }}
                  >
                    <div style={{
                      width: 28,
                      height: (cfg as any).isPolygon ? 20 : Math.max(cfg.lineWidth * 2, 4),
                      borderRadius: (cfg as any).isPolygon ? 4 : cfg.lineWidth,
                      background: (cfg as any).isPolygon
                        ? `repeating-linear-gradient(45deg, ${cfg.color}, ${cfg.color} 2px, transparent 2px, transparent 5px)`
                        : cfg.color,
                      border: (cfg as any).isPolygon ? `1.5px solid ${cfg.color}` : "none",
                      boxShadow: `0 0 6px ${cfg.color}88`,
                    }} />
                    <span style={{
                      fontSize: "0.58rem",
                      fontWeight: 700,
                      letterSpacing: "0.03em",
                      opacity: highlighted ? 1 : 0.7,
                    }}>
                      {cfg.label}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          {/* Draw tool button */}
          <div
            onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); handleDrawPointerDown(); }}
            onPointerUp={handleDrawPointerUp}
            onPointerLeave={() => {
              if (drawPickerTimer.current) {
                clearTimeout(drawPickerTimer.current);
                drawPickerTimer.current = null;
              }
            }}
            style={{ touchAction: "none" }}
          >
            <ActionBarButton
              active={drawMode}
              activeColor={DRAW_TOOL_CONFIG[drawToolType].activeColor}
              label={DRAW_TOOL_CONFIG[drawToolType].label}
              onClick={() => {}}
            >
              <IconDrawDiscontinuity />
            </ActionBarButton>
          </div>
          <ActionBarButton
            active={measureMode}
            activeColor="#44aaff"
            label="Dip/Dir"
            onClick={handleToggleMeasure}
          >
            <IconMeasureDipDir />
          </ActionBarButton>
          {hasOrientationSensor && (
            <ActionBarButton
              active={arPhotoActive}
              activeColor="#44cc88"
              label={arPhotoActive ? "Snap" : "Photo"}
              onClick={handleTakePhoto}
            >
              <IconTakePhoto />
            </ActionBarButton>
          )}
        </div>
        )}

        {/* Station slider */}
        {data && (
          <div style={{ width: "100%" }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.75rem", marginBottom: "0.3rem" }}>
              <span style={{ opacity: 0.4 }}>0 m</span>
              <span style={{ fontWeight: 700 }}>{station.toFixed(0)} m</span>
              <span style={{ opacity: 0.4 }}>{Math.floor(data.totalLength / RING_SPACING) * RING_SPACING} m</span>
            </div>
            <input
              type="range"
              min={0}
              max={data.totalLength}
              step={1}
              value={station}
              onChange={(e) => setStation(parseFloat(e.target.value))}
              style={{ display: "block", width: "100%", height: 24, cursor: "pointer", margin: 0, boxSizing: "border-box", accentColor: NGI_RED, colorScheme: "dark" }}
            />
            {!hasOrientationSensor && (
              <div style={{ textAlign: "center", fontSize: "0.65rem", opacity: 0.35, marginTop: "0.2rem" }}>
                Ctrl+Scroll to step
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Draw Plane overlay — captures a hand-drawn line to define plane normal ── */}
      {rbfDrawingPlane && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            cursor: "crosshair",
            background: "rgba(0,0,0,0.35)",
            touchAction: "none",
            userSelect: "none",
            WebkitUserSelect: "none",
          }}
          onPointerDown={(e) => {
            (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
            e.preventDefault();
            const x = e.clientX;
            const y = e.clientY;
            setRbfDrawLine({ x1: x, y1: y, x2: x, y2: y });
          }}
          onPointerMove={(e) => {
            e.preventDefault();
            setRbfDrawLine((prev) => {
              if (!prev) return prev;
              return { ...prev, x2: e.clientX, y2: e.clientY };
            });
          }}
          onPointerUp={(e) => {
            e.preventDefault();
            try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch {}
            setRbfDrawLine((prev) => {
              if (!prev) { setRbfDrawingPlane(false); return null; }
              const dx = prev.x2 - prev.x1;
              const dy = prev.y2 - prev.y1;
              const len = Math.sqrt(dx * dx + dy * dy);
              if (len < 20) { setRbfDrawingPlane(false); return null; }
              // 2D perpendicular to drawn line (in screen space): rotate 90 degrees
              const perpX = -dy / len;
              const perpY = dx / len;
              // Convert screen-space perpendicular direction to 3D world normal using camera orientation
              const q = cameraQuatRef.current;
              const right = new THREE.Vector3(1, 0, 0).applyQuaternion(q);
              const up = new THREE.Vector3(0, 1, 0).applyQuaternion(q);
              const normal = new THREE.Vector3(
                perpX * right.x - perpY * up.x,
                perpX * right.y - perpY * up.y,
                perpX * right.z - perpY * up.z,
              ).normalize();
              setRbfDrawnNormal([normal.x, normal.y, normal.z]);
              setRbfDrawingPlane(false);
              return null;
            });
          }}
          onPointerCancel={(e) => {
            try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch {}
            setRbfDrawingPlane(false);
            setRbfDrawLine(null);
          }}
        >
          {/* Instruction text */}
          <div style={{
            position: "absolute", top: 40, left: "50%", transform: "translateX(-50%)",
            color: "#fff", fontSize: "0.85rem", fontWeight: 600,
            background: "rgba(0,0,0,0.6)", padding: "8px 16px", borderRadius: 8,
            pointerEvents: "none",
          }}>
            Draw a line along the plane strike direction
          </div>
          {/* Drawn line SVG */}
          {rbfDrawLine && (
            <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}>
              <line
                x1={rbfDrawLine.x1} y1={rbfDrawLine.y1}
                x2={rbfDrawLine.x2} y2={rbfDrawLine.y2}
                stroke="#00ff88" strokeWidth={3} strokeLinecap="round"
              />
              {/* Show perpendicular indicator */}
              {(() => {
                const dx = rbfDrawLine.x2 - rbfDrawLine.x1;
                const dy = rbfDrawLine.y2 - rbfDrawLine.y1;
                const len = Math.sqrt(dx * dx + dy * dy);
                if (len < 20) return null;
                const mx = (rbfDrawLine.x1 + rbfDrawLine.x2) / 2;
                const my = (rbfDrawLine.y1 + rbfDrawLine.y2) / 2;
                const perpX = -dy / len * 30;
                const perpY = dx / len * 30;
                return <line x1={mx - perpX} y1={my - perpY} x2={mx + perpX} y2={my + perpY} stroke="#ff4488" strokeWidth={2} strokeDasharray="4 3" />;
              })()}
            </svg>
          )}
          {/* Cancel button */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setRbfDrawingPlane(false); setRbfDrawLine(null); }}
            style={{
              position: "absolute", top: 12, right: 12,
              background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: 8, color: "#fff", padding: "10px 18px", cursor: "pointer", fontSize: "0.85rem",
              touchAction: "manipulation",
            }}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
